/* ==========================================================================
   Flower Station - AI florist chat widget
   --------------------------------------------------------------------------
   Front end only. Talks to the hosted RAG bot (POST /chat/stream, with
   POST /chat as a fallback and GET /chat/history for resume).

   THEME SAFETY - this file is added to a duplicate of a live storefront:

   1. Everything renders inside a shadow root, so the theme's CSS cannot
      reach these elements and this CSS cannot leak into the storefront.
      (The theme styles bare `button` and `input` aggressively - shadow DOM
      is the only reliable defence.)
   2. The shadow host is a fixed, full-viewport, pointer-events:none layer
      appended to document.body. Every layout property on it is set with
      `!important` via setProperty(), because the theme uses `!important`
      itself and has previously applied display:none to an injected widget
      container - the script ran, the nodes existed, nothing appeared.
   3. The host carries transform:none / filter:none. Any ancestor with a
      transform becomes the containing block for position:fixed descendants,
      which silently drags the launcher off-screen.
   4. Two globals are added, and only two: window.FSAI_CHAT_LOADED, and
      window.FSAI - the interface assets/fsai-livechat.js attaches through.
   5. No API value ever touches innerHTML. Nodes are built and textContent
      is set; hrefs are scheme-checked. Customers write the questions and
      the answers echo them, so every field is treated as hostile.

   Configured from the script tag (see snippets/fsai-chat-widget.liquid):
     data-api            bot base URL, required
     data-side           "right" (default) | "left" - which edge to hug
     data-offset-side    distance from that edge, default 18px
     data-hello-hide-on  paths where the greeting bubble is not shown

   PLACEMENT - why the right edge at MID-HEIGHT and not a bottom corner:
   the storefront already floats a Brevo chat bubble and a green CALL NOW
   button, both anchored to the BOTTOM of the viewport, stacking about 240px
   up from it on the right. Bottom-left is no better - it lands on the review
   /trust strip and on Shopify's own consent banner in some regions. Anchoring
   to the vertical centre of the viewport puts this launcher on an axis those
   bottom-anchored elements cannot reach at any sensible window size, and a
   max-height media query lifts it clear on a short viewport.
   ========================================================================== */

(function () {
  'use strict';

  if (window.FSAI_CHAT_LOADED) return;     // guard a double include
  window.FSAI_CHAT_LOADED = true;

  /* Feature modules (assets/fsai-livechat.js) are separate files and may run
     before this one has built anything. They leave a callback in onReady and
     it is called with the interface once the widget exists. Created now, at
     script evaluation, precisely so it is there before any module looks.

     One namespace, filled in later rather than replaced, so the count of
     globals this file adds stays at two and a module holding a reference
     never finds it swapped underneath. */
  window.FSAI = window.FSAI || {};
  window.FSAI.onReady = window.FSAI.onReady || [];

  var script = document.currentScript || (function () {
    var all = document.getElementsByTagName('script');
    return all[all.length - 1];
  })();

  function attr(name, fallback) {
    var v = script && script.getAttribute(name);
    return (v === null || v === undefined || v === '') ? fallback : v;
  }

  var API = String(attr('data-api', '')).replace(/\/+$/, '');
  var SIDE = attr('data-side', 'right') === 'left' ? 'left' : 'right';
  var OFFSET_SIDE = attr('data-offset-side', '24px');   // matches #support-call

  /* data-hello-hide-on ------------------------------------------------------
     Comma-separated paths where her "need a hand?" greeting is NOT shown -
     the launcher itself stays. These are the pages whose own checkout or
     continue button sits along the bottom of the screen, where the greeting
     (which floats above everything) would otherwise cover it.

     A path matches itself and anything below it, so "/pages/product-addons"
     also covers "/pages/product-addons/". The market prefix (/en-us/...) is
     stripped first, so one entry covers every market. */
  var HELLO_HIDE_ON = String(attr('data-hello-hide-on', ''))
    .split(',')
    .map(function (path) { return path.trim().replace(/\/+$/, ''); })
    .filter(Boolean);

  /* data-motion ------------------------------------------------------------
     "respect" (default) honours prefers-reduced-motion, which Windows turns on
     via Settings > Accessibility > Visual effects > Animation effects and macOS
     via Accessibility > Display > Reduce motion. With that setting on, every
     decorative animation here is switched off by the browser - the wave, the
     blink, the sheen - and the widget looks frozen even though it is working
     exactly as asked.

     "always" overrides that. Use it to check the animations on a machine that
     has the OS setting on; leaving it on in production overrides a genuine
     accessibility preference, so that is a deliberate choice, not a default. */
  var MOTION = attr('data-motion', 'respect') === 'always' ? 'always' : 'respect';

  /* Live support ----------------------------------------------------------
     The customer CHOOSES between Julie and a person - nothing escalates on
     its own. Live chat is a separate service with its own conversation store;
     the two only meet at the chooser, in the browser.

     WHOSE CLOCK: nobody's, here. Opening hours are decided server-side and
     read from GET /live/status. Computing them in the browser was wrong - a
     shopper in New York would see their own working day, not London's - and
     the server also knows whether an agent is actually signed in, which no
     amount of clock arithmetic can tell us. */
  var PHONE = attr('data-phone', '020 7724 7525');
  var EMAIL = attr('data-email', 'info@flowerstation.co.uk');
  /* Prefixed onto the dialled number in place of its trunk 0 - see telHref().
     Set it empty to dial exactly what data-phone says. */
  var DIAL_CODE = attr('data-dial-code', '+44');
  var LIVE_ENABLED = attr('data-live-enabled', 'true') !== 'false';
  var LIVE_API = String(
    attr('data-live-api', 'https://live-chat-support-production.up.railway.app')
  ).replace(/\/+$/, '');

  /* wss, never ws: the storefront is HTTPS and a browser drops a plaintext
     socket as mixed content without surfacing an error anyone would notice. */
  function liveSocketUrl(conversationId) {
    return LIVE_API.replace(/^http/i, 'ws') +
           '/ws/customer/' + encodeURIComponent(conversationId);
  }

  var LIVE_ID_KEY = 'fsai_live_id';
  var LIVE_SEEN_KEY = 'fsai_live_seen';   // an agent has actually spoken in it
  var LIVE_MAIL_KEY = 'fsai_live_mail';   // the address this chat replies to
  var LIVE_OWNER_KEY = 'fsai_live_owner'; // scrambled id of the Shopify customer
  var LIVE_AT_KEY = 'fsai_live_at';       // last activity, for the guest bound
  var ADOPT_MS = 30 * 60 * 1000;          // matches the service's own idle rule
  var LIVE_MAX_CHARS = 4000;      // server truncates beyond this

  /* Takes a number written any way at all and returns the tel: link for it.
     Only a national number starting with the trunk 0 gets the country code:
     one already written in full (+44..., 00 44...) is left exactly as it is,
     so this cannot produce +44044 or stack a second code on top. */
  function dialable(number) {
    var digits = String(number || '').replace(/^tel:/i, '').replace(/[^0-9+]/g, '');
    if (DIAL_CODE && digits.charAt(0) === '0' && digits.charAt(1) !== '0') {
      digits = DIAL_CODE + digits.slice(1);
    }
    return 'tel:' + digits;
  }

  function telHref() { return dialable(PHONE); }

  /* "17:00" out of an ISO timestamp, in the shop's own offset as the server
     sent it - deliberately NOT re-zoned through the visitor's locale. */
  function clockFromIso(iso) {
    var m = /T(\d{2}):(\d{2})/.exec(String(iso || ''));
    if (!m) return '';
    var h = parseInt(m[1], 10), min = m[2];
    var suffix = h < 12 ? 'am' : 'pm';
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + (min === '00' ? '' : ':' + min) + suffix;
  }

  /* "today at 9am" / "tomorrow at 9am" / "Friday at 9am", worked out by
     comparing dates only - the time itself comes from the server string. */
  function whenFromIso(iso) {
    var time = clockFromIso(iso);
    if (!time) return '';
    var when = new Date(iso);
    if (isNaN(when.getTime())) return 'at ' + time;
    var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var midnight = function (d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); };
    var gap = Math.round((midnight(when) - midnight(new Date())) / 86400000);
    if (gap <= 0) return 'today at ' + time;
    if (gap === 1) return 'tomorrow at ' + time;
    return days[when.getDay()] + ' at ' + time;
  }

  /* Who is shopping ------------------------------------------------------
     Shopify already tells the theme this, so there is no sign-in flow to
     build - Liquid hands over `customer` and the snippet passes it through.

     This name is for SAYING HELLO AND NOTHING ELSE. It arrives through the
     browser, so it can be edited by anyone with devtools open. No backend may
     ever accept an identity from here to look an order up; that has to come
     from a Shopify App Proxy, where Shopify signs the customer id itself. */
  var SIGNED_IN = attr('data-signed-in', 'false') === 'true';
  /* Read before any of it is used - see guardPreviousCustomer below. */
  var CUSTOMER_NAME = String(attr('data-customer-name', '')).trim().slice(0, 40);
  /* Full name and email, sent once when a live conversation is created so the
     agent sees who they are talking to instead of "Visitor 4821". Separate
     from data-customer-name, which is the FIRST name and is only ever used to
     greet them - "Hello Bilal Ahmed" would read worse than "Hello Bilal". */
  var CUSTOMER_FULLNAME = String(attr('data-customer-fullname', '')).trim().slice(0, 80);
  var CUSTOMER_EMAIL = String(attr('data-customer-email', '')).trim().slice(0, 120);
  var LOGIN_URL = attr('data-login-url', '');
  /* The team's picture in live support. Theme-controlled like every other
     data-* here, but it ends up in an img src, so only the shapes Shopify's
     asset_url actually produces are accepted - never a javascript: url. */
  /* Anything that ends up in an img src passes through here - the theme's own
     logo and, now, pictures of the team that arrive over the socket. Only the
     shapes a real image url takes; never javascript:, never data:. */
  function safeImageUrl(value) {
    var url = String(value || '').trim();
    return /^(https:\/\/|\/\/|\/)[^"'<>\s]+$/.test(url) ? url : '';
  }

  var AGENT_AVATAR_URL = safeImageUrl(attr('data-agent-avatar', ''));

  /* Signing in leaves the page, so remember to come back into the widget. */
  var RETURNING_KEY = 'fsai_returning';
  /* Half-typed message, kept across a navigation. Everything else about the
     conversation already survives - losing the sentence they were in the
     middle of is the one thing that would not. */
  var DRAFT_KEY = 'fsai_draft';

  function firstName() {
    // guard against a name that is only punctuation or markup-ish junk
    return /[A-Za-zÀ-ÿ]/.test(CUSTOMER_NAME) ? CUSTOMER_NAME : '';
  }

  /* Who to tell the agent they are speaking to. A courtesy, not a credential:
     Shopify has authenticated them for the STOREFRONT, but this travels in a
     page attribute that anyone can edit, so the live-chat service must never
     look an order up from it. That needs an App Proxy, where Shopify signs the
     customer id itself. Omitted entirely for guests, who keep the name the
     service generates. */
  /* One definition, used by the gate and by the enquiry form, so a shape one
     of them accepts cannot be refused by the other. */
  /* ===================================================================
     A shared computer.

     Person A has a live chat. They sign out of Shopify. Person B signs in and
     opens the widget - and reads A's transcript, because the conversation id
     is still in this browser's storage and the server, quite correctly, hands
     over the transcript for any valid id it is shown. The browser is the only
     place that knows the signed-in customer changed.

     Three keys cross that boundary, not one:
       fsai_live_id     the transcript
       fsai_session_id  the AI bot keys its own history off this, so Julie can
                        carry A's context into B's conversation without a word
                        of it ever appearing on screen
       fsai_draft       A's half-typed sentence, restored into B's box
     ===================================================================

     Not a cryptographic hash and not pretending to be: it exists so the
     previous customer's address is not sitting in storage to be read, and it
     is only ever compared for equality. Anyone who can run this can also read
     the signed-in customer off the page. */
  function scramble(value) {
    var str = String(value || '').trim().toLowerCase();
    if (!str) return '';
    var h1 = 0x811c9dc5, h2 = 0x1000193;
    for (var i = 0; i < str.length; i++) {
      var c = str.charCodeAt(i);
      h1 = (h1 ^ c) >>> 0;
      h1 = (h1 + ((h1 << 1) + (h1 << 4) + (h1 << 7) + (h1 << 8) + (h1 << 24))) >>> 0;
      h2 = (((h2 << 5) - h2) + c) >>> 0;
    }
    return h1.toString(36) + '.' + h2.toString(36);
  }

  /* Who the browser belongs to right now. Null for a guest. */
  function currentOwner() {
    return SIGNED_IN && CUSTOMER_EMAIL ? scramble(CUSTOMER_EMAIL) : '';
  }

  function dropChatKeys() {
    clearStore(LIVE_ID_KEY);
    clearStore(LIVE_SEEN_KEY);
    clearStore(LIVE_MAIL_KEY);
    clearStore(LIVE_OWNER_KEY);
    clearStore(LIVE_AT_KEY);
    /* The quiet one. Julie's history lives server-side against this id, so
       leaving it behind hands the next person the previous conversation's
       context even though nothing is on screen. */
    clearStore(SESSION_KEY);
    clearStore(DRAFT_KEY);
  }

  /* Runs once, before anything is read into the widget's state. */
  function guardPreviousCustomer() {
    var stored = readStore(LIVE_OWNER_KEY) || '';
    var now = currentOwner();
    if (!readStore(LIVE_ID_KEY) && !readStore(SESSION_KEY)) return;

    if (stored) {
      // a chat that belonged to a signed-in customer
      if (stored !== now) dropChatKeys();
      return;
    }
    /* An ownerless chat: started by a guest. If somebody signs in now it may
       be the same person finishing what they started, or the next person on a
       shop computer. Recent enough is the only tell we get, and the service
       refuses to identify anything idle beyond the same window. */
    if (now) {
      var at = parseInt(readStore(LIVE_AT_KEY) || '0', 10);
      if (!at || (Date.now() - at) > ADOPT_MS) dropChatKeys();
    }
  }

  function looksLikeEmail(value) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || '').trim());
  }

  function visitorIdentity() {
    var who = {};
    if (!SIGNED_IN) return who;
    var name = CUSTOMER_FULLNAME || CUSTOMER_NAME;
    if (/[A-Za-zÀ-ÿ]/.test(name)) who.visitor_name = name;
    // a half-empty field is worse than none - it just gives the agent junk
    if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(CUSTOMER_EMAIL)) {
      who.visitor_email = CUSTOMER_EMAIL;
    }
    return who;
  }

  /* Selectors for the storefront's own floating controls in the right-hand
     column. The launcher is placed above whichever of these is tallest so it
     can never sit on top of one. Read-only - nothing here is modified. */
  var STACK_SELECTORS = ['#support-call', '#brevo-conversations'];
  var STACK_GAP = 14;        // breathing room between the launcher and the stack
  var STACK_FLOOR = 18;      // where the launcher sits when the column is empty
  var STACK_MAX_ITEM = 260;  // taller than this means an expanded panel, not a button
  /* Smaller than any floating button worth stepping around, larger than the
     worst mid-scroll measurement error on a phone. See measureStack(). */
  var STACK_HYSTERESIS = 56;

  var SESSION_KEY = 'fsai_session_id';
  var OPEN_KEY = 'fsai_panel_open';
  var SENDS_KEY = 'fsai_recent_sends';
  var REQUEST_TIMEOUT_MS = 60000;

  /* Client-side burst throttle -------------------------------------------
     The bot rate-limits at 12 requests/minute and answers with HTTP 429 in
     the normal response shape. In a browser we cannot READ that 429: the
     limiter runs as the outermost middleware and short-circuits before
     CORSMiddleware, so the response carries no Access-Control-Allow-Origin
     and the browser rejects it as a network failure - which would tell a
     customer who typed quickly that the shop is broken.

     So we trip first, locally, and say the friendly thing ourselves. The
     budget is 11 rather than 12 because the CORS preflight also spends one
     (Access-Control-Max-Age is 86400, so only the first message pays it).
     If the server ever trips first anyway - a shared NAT puts strangers on
     one IP - the opaque-failure branch in submit() still prefers the
     rate-limit wording over a connection error. */
  var BURST_LIMIT = 11;
  var BURST_WINDOW_MS = 60000;

  /* ---------------------------------------------------------------- storage
     localStorage, NOT sessionStorage: sessionStorage is per-tab and dies the
     moment the customer navigates to a product page, which is exactly the
     case we need to survive. Every read and write is wrapped - localStorage
     throws outright in some privacy modes and an unguarded call would take
     the whole widget down. */
  function readStore(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function writeStore(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* full or blocked */ }
  }
  function clearStore(key) {
    try { window.localStorage.removeItem(key); } catch (e) { /* blocked */ }
  }

  /* Before a single stored value is read into the widget. If the person at
     this browser is not the one this chat belongs to, none of it loads. */
  guardPreviousCustomer();

  /* Send timestamps inside the rolling burst window. The `t <= now` guard
     matters: without it a clock that jumps backwards makes every stored
     timestamp look like the future and the throttle never clears. */
  function recentSends() {
    var now = Date.now();
    var list;
    try { list = JSON.parse(readStore(SENDS_KEY) || '[]'); } catch (e) { list = []; }
    if (!Array.isArray(list)) list = [];
    return list.filter(function (t) {
      return typeof t === 'number' && isFinite(t) && t <= now && now - t < BURST_WINDOW_MS;
    });
  }

  function recordSend(list) {
    list.push(Date.now());
    writeStore(SENDS_KEY, JSON.stringify(list.slice(-BURST_LIMIT * 2)));
  }

  /* ------------------------------------------------------------------- CSS
     Lives inside the shadow root. Every class is `fsai-` prefixed and there is
     not one bare element selector or `*` rule anywhere, so nothing here can
     reach the storefront even in principle.

     Type and colour come from the shop itself: Frank Ruhl Libre for display
     (self-hosted by the theme at base.css:12, so the @font-face is already
     registered document-wide and reaches into this shadow root), Open Sans for
     body, #2d2d2d ink, the warm creams of the hero, the brown of "Shop Now"
     and #f599a7 rose for the accent. */
  /* The stylesheet lives in assets/fsai-chat.css and is attached to the
     SHADOW ROOT, never to the page - see build(). A <link> in theme.liquid
     would put it in the global scope and the isolation would be gone. */
  var CSS_URL = attr('data-css', '');

  /* ---- icons (static, authored here - never built from API data) ---- */
  var ICON_BLOOM =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="9" r="2.4"/>' +
    '<path d="M12 6.6c0-2 1.1-3.6 2.6-3.6S17 4.3 17 5.7c0 1.7-1.9 2.8-3.4 3.1"/>' +
    '<path d="M12 6.6c0-2-1.1-3.6-2.6-3.6S7 4.3 7 5.7c0 1.7 1.9 2.8 3.4 3.1"/>' +
    '<path d="M14.4 9c1.8-.9 3.7-.6 4.4.7.7 1.3-.2 3-1.9 3.6-1.6.6-3.3-.5-4.1-1.8"/>' +
    '<path d="M9.6 9c-1.8-.9-3.7-.6-4.4.7-.7 1.3.2 3 1.9 3.6 1.6.6 3.3-.5 4.1-1.8"/>' +
    '<path d="M12 11.4V21"/><path d="M12 16.5c1.6 0 3-1.1 3.4-2.6"/>' +
    '</svg>';

  var ICON_CLOSE =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" ' +
    'stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  var ICON_SEND =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M5 12h13"/><path d="M12.5 5.5L19 12l-6.5 6.5"/></svg>';

  var ICON_LEAF =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M5 19c0-7 5-12 14-12 0 9-5 13-11 13H5z"/><path d="M5 19c3-4 6-6 10-7.5"/></svg>';

  var ICON_BACK =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M19 12H6"/><path d="M11.5 5.5L5 12l6.5 6.5"/></svg>';

  /* two florists at a counter - reads as "a person", not "a robot" */
  var ICON_AGENT =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<circle cx="9" cy="8" r="3.2"/>' +
    '<path d="M3.4 20c.5-3.6 2.7-5.6 5.6-5.6s5.1 2 5.6 5.6"/>' +
    '<circle cx="16.8" cy="9.4" r="2.4"/>' +
    '<path d="M15 14.7c.6-.3 1.2-.4 1.8-.4 2.2 0 3.9 1.5 4.3 4.2"/></svg>';

  var ICON_ORDER =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M4.5 8.5l7.5-4 7.5 4v7l-7.5 4-7.5-4z"/><path d="M4.5 8.5l7.5 4 7.5-4"/>' +
    '<path d="M12 12.5v7"/></svg>';

  var ICON_ARROW =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M6 12h11"/><path d="M12 6.5l5.5 5.5L12 17.5"/></svg>';

  var ICON_CLIP =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M20 11.5l-8 8a5 5 0 01-7-7l8.5-8.5a3.3 3.3 0 014.7 4.7L9.6 17.3a1.6 1.6 0 01-2.3-2.3l7.8-7.8"/></svg>';

  var ICON_MAIL =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<rect x="3" y="5.5" width="18" height="13" rx="2"/><path d="M3.5 7.2l8.5 5.8 8.5-5.8"/></svg>';

  var ICON_PHONE =
    '<svg class="fsai-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M6.3 3.5h3l1.5 3.8-1.9 1.2a11 11 0 005.6 5.6l1.2-1.9 3.8 1.5v3a1.8 1.8 0 01-2 1.8' +
      'A15.5 15.5 0 014.5 5.5a1.8 1.8 0 011.8-2z"/></svg>';



  /* ---- Julie -------------------------------------------------------------
     Drawn inline rather than shipped as an image: no extra request, nothing for
     an ad blocker or the CSP to object to, crisp at any size. Flat fills only -
     no gradients, so no <defs> ids to collide when the portrait is stamped out
     several times in one shadow root.

     A small WAVING FIGURE, not a head-and-shoulders portrait. That matters: a
     circular crop puts her shoulders at the bottom of the frame, so an arm
     bolted onto the top-right corner has nothing to attach to and reads as a
     hand floating in space - which is exactly how the last three attempts
     looked. She is zoomed out instead, with the raised arm growing from her
     shoulder and the whole gesture sitting comfortably inside the circle
     (nothing further than 20 units from centre, against a 31 unit clip).

     Two moving parts, both driven from CSS:
       .fsai-eye   both eyes, so she can blink
       .fsai-hand  her raised arm, shown ONLY on the launcher where it waves
                   you in; a raised arm beside every message would be odd. */
  var AGENT_AVATAR =
    '<svg class="fsai-mark" viewBox="0 0 64 64" aria-hidden="true" focusable="false">' +
      '<circle cx="32" cy="32" r="32" fill="#5f4633"/>' +
      /* shoulders, in the shop's cream so they read against the brown */
      '<path d="M12 64c1.6-10.3 9.4-16 20-16s18.4 5.7 20 16z" fill="#f4e7db"/>' +
      '<path d="M28 48.4c1.3 1.9 2.6 2.8 4 2.8s2.7-.9 4-2.8" stroke="#d8c6b4" ' +
        'stroke-width="1.4" fill="none" stroke-linecap="round"/>' +
      /* neck */
      '<path d="M28.6 40.4h6.8v5.6c0 1.5-1.5 2.6-3.4 2.6s-3.4-1.1-3.4-2.6z" fill="#dCA985"/>' +
      /* hair mass, behind the face */
      '<path d="M32 12.2c7.9 0 12.8 5.3 12.8 13.6 0 2.5-.3 4.8-.9 6.9h-2.3c.5-2.1.6-4.1.3-6' +
        'c-.4-2.7-1.5-4.5-2.7-5.7-2.1 1.6-4.7 2.5-7.2 2.5s-5.1-.9-7.2-2.5c-1.2 1.2-2.3 3-2.7 5.7' +
        'c-.3 1.9-.2 3.9.3 6h-2.3c-.6-2.1-.9-4.4-.9-6.9C19.2 17.5 24.1 12.2 32 12.2z" ' +
        'fill="#33261a"/>' +
      /* face */
      '<ellipse cx="32" cy="29" rx="10.4" ry="11.4" fill="#f0cdb0"/>' +
      '<path d="M22.6 24c2.3-4.2 5.6-6.5 9.4-6.5s7.1 2.3 9.4 6.5c-2.8-2.4-6-3.6-9.4-3.6' +
        's-6.6 1.2-9.4 3.6z" fill="#33261a"/>' +
      '<circle class="fsai-eye" cx="28.2" cy="28.8" r="1.45" fill="#3a2a1c"/>' +
      '<circle class="fsai-eye" cx="35.8" cy="28.8" r="1.45" fill="#3a2a1c"/>' +
      '<path d="M29.2 34.2c.9 1 1.8 1.5 2.8 1.5s1.9-.5 2.8-1.5" stroke="#b5765a" ' +
        'stroke-width="1.4" fill="none" stroke-linecap="round"/>' +
      /* headset - what the job looks like at this size */
      '<path d="M21.2 30.4v-3c0-6 4.8-10.8 10.8-10.8s10.8 4.8 10.8 10.8v3" ' +
        'stroke="#f4e7db" stroke-width="2.1" fill="none" stroke-linecap="round"/>' +
      '<rect x="18.9" y="29.2" width="4.8" height="7.6" rx="2.4" fill="#f4e7db"/>' +
      '<rect x="40.3" y="29.2" width="4.8" height="7.6" rx="2.4" fill="#f4e7db"/>' +
      '<path d="M21.3 36.8c0 4.3 3.2 7 6.9 7.5" stroke="#f4e7db" stroke-width="1.5" ' +
        'fill="none" stroke-linecap="round"/>' +
      '<circle cx="29.1" cy="44.4" r="1.5" fill="#f4e7db"/>' +
    '</svg>';

  var AVATAR =
    '<svg class="fsai-face" viewBox="0 0 64 64" aria-hidden="true" focusable="false">' +
      '<circle cx="32" cy="32" r="32" fill="#d9647c"/>' +
      /* hair mass, behind everything */
      '<path d="M27 13.6c8.6 0 14 6 14 15.2 0 5.4-.8 9.2-.8 13.2 0 2.3-1.5 3.6-3.2 3.6' +
        '1.1-4.3 1.2-8.3.8-11.6-.5-4-2-6.4-3.6-7.9-1.8 1.5-4.6 2.4-7.2 2.4s-5.4-.9-7.2-2.4' +
        'c-1.6 1.5-3.1 3.9-3.6 7.9-.4 3.3-.3 7.3.8 11.6-1.7 0-3.2-1.3-3.2-3.6 0-4-.8-7.8-.8-13.2' +
        'C13 19.6 18.4 13.6 27 13.6z" fill="#43301f"/>' +
      /* neck, then her blouse */
      '<path d="M23.7 40.6h6.6v5.4c0 1.4-1.5 2.5-3.3 2.5s-3.3-1.1-3.3-2.5z" fill="#e8c7b1"/>' +
      '<path d="M9 64c1.4-10.5 8.8-16.4 18-16.4S43.6 53.5 45 64z" fill="#5f4633"/>' +
      '<path d="M23 48.8c1.3 1.9 2.7 2.8 4 2.8s2.7-.9 4-2.8" stroke="#fdfaf6" ' +
        'stroke-width="1.5" fill="none" stroke-linecap="round"/>' +
      /* her raised arm - launcher only, see .fsai-hand. Drawn after the blouse
         so the sleeve overlaps her shoulder and reads as joined to it. */
      '<g class="fsai-hand">' +
        '<path d="M38.4 50.2L44.8 41.4" stroke="#5f4633" stroke-width="7.2" ' +
          'stroke-linecap="round" fill="none"/>' +
        '<path d="M45.1 41.8L46.3 31.4" stroke="#f2c9ae" stroke-width="6.1" ' +
          'stroke-linecap="round" fill="none"/>' +
        '<circle cx="42.4" cy="28.4" r="2.3" fill="#f2c9ae"/>' +
        '<rect x="42.3" y="24.3" width="8.6" height="8.2" rx="4.1" fill="#f2c9ae"/>' +
        '<rect x="43.1" y="19.4" width="7.2" height="7.4" rx="3.6" fill="#f2c9ae"/>' +
        '<path d="M45.3 20.8v3.9" stroke="#dfb094" stroke-width="1" ' +
          'stroke-linecap="round" fill="none"/>' +
        '<path d="M48.1 20.8v3.9" stroke="#dfb094" stroke-width="1" ' +
          'stroke-linecap="round" fill="none"/>' +
      '</g>' +
      /* face */
      '<ellipse cx="27" cy="28" rx="10.6" ry="11.8" fill="#f4d9c6"/>' +
      '<path d="M16.9 22.4c2.4-4.4 5.9-6.8 10.1-6.8s7.7 2.4 10.1 6.8c-3-2.5-6.4-3.8-10.1-3.8' +
        's-7.1 1.3-10.1 3.8z" fill="#43301f"/>' +
      '<ellipse cx="20.6" cy="30.6" rx="2.4" ry="1.6" fill="#f2a3ac" opacity=".55"/>' +
      '<ellipse cx="33.4" cy="30.6" rx="2.4" ry="1.6" fill="#f2a3ac" opacity=".55"/>' +
      '<path d="M20.4 24.4c1.3-.9 3-.9 4.2-.2" stroke="#43301f" stroke-width="1.3" ' +
        'fill="none" stroke-linecap="round"/>' +
      '<path d="M29.4 24.2c1.2-.7 2.9-.7 4.2.2" stroke="#43301f" stroke-width="1.3" ' +
        'fill="none" stroke-linecap="round"/>' +
      '<ellipse class="fsai-eye" cx="22.8" cy="28.4" rx="1.6" ry="2" fill="#33261c"/>' +
      '<ellipse class="fsai-eye" cx="31.2" cy="28.4" rx="1.6" ry="2" fill="#33261c"/>' +
      '<path d="M27 29.8v2.1" stroke="#dbb49b" stroke-width="1.2" stroke-linecap="round" fill="none"/>' +
      '<path d="M23.9 34.6c1.9 1.9 4.3 1.9 6.2 0" stroke="#a9635c" stroke-width="1.6" ' +
        'fill="none" stroke-linecap="round"/>' +
      /* a bloom tucked behind her ear */
      '<circle cx="37.4" cy="18.4" r="2.2" fill="#f7b3bd"/>' +
      '<circle cx="39.8" cy="20.3" r="1.9" fill="#f599a7"/>' +
      '<circle cx="35.2" cy="20.9" r="1.9" fill="#f599a7"/>' +
      '<circle cx="37.7" cy="22.3" r="1.6" fill="#efa4b0"/>' +
      '<circle cx="37.6" cy="20.4" r="1.1" fill="#fff4f5"/>' +
    '</svg>';

  var STARTERS = [
    'Flowers for my mum',
    'Romantic, under £50',
    'New baby congratulations',
    'Can you deliver today?'
  ];

  /* Static shell. No interpolation of anything dynamic - the only innerHTML in
     this file, and every string in it is authored above. */
  var SHELL =
    '<div class="fsai-layer">' +
      /* Label sits inboard of the mark: on a right-edge pill the icon reads
         as the anchor and the words open away from the screen edge. */
      '<button class="fsai-launcher" type="button" aria-haspopup="dialog" ' +
              'aria-expanded="false" aria-label="Chat with us">' +
        '<span class="fsai-launcher-label">Chat with us</span>' +
        '<span class="fsai-launcher-mark">' + AVATAR + '</span>' +
        '<span class="fsai-unread" hidden aria-hidden="true"></span>' +
      '</button>' +
      '<div class="fsai-hello" hidden>' +
        '<button class="fsai-hello-open" type="button">' +
          '<span class="fsai-hello-text">' +
            '<span class="fsai-hello-name">Need a hand?</span>' +
            '<span class="fsai-hello-sub">Chat with Julie or our team</span>' +
          '</span>' +
        '</button>' +
        '<button class="fsai-hello-close" type="button" aria-label="Dismiss">' +
          ICON_CLOSE +
        '</button>' +
      '</div>' +
      '<div class="fsai-panel" role="dialog" aria-label="Flower Station help" hidden>' +
        '<div class="fsai-accent"></div>' +
        '<div class="fsai-head">' +
          '<button class="fsai-back" type="button" aria-label="Back" hidden>' + ICON_BACK + '</button>' +
          '<span class="fsai-head-mark">' + AVATAR + '</span>' +
          '<span class="fsai-head-text">' +
            '<span class="fsai-head-title">Flower Station</span>' +
            '<span class="fsai-head-sub">' +
              '<span class="fsai-dot-live"></span>How can we help?' +
            '</span>' +
          '</span>' +
          '<button class="fsai-close" type="button" aria-label="Close chat">' + ICON_CLOSE + '</button>' +
        '</div>' +

        /* view 1 - the customer picks who they want to talk to */
        '<div class="fsai-view fsai-choose">' +
          '<span class="fsai-choose-title">How can we help?</span>' +
          '<span class="fsai-choose-sub">Ask our AI florist for instant answers, or talk to one of the team.</span>' +
          '<button class="fsai-opt fsai-opt--ai" type="button">' +
            '<span class="fsai-opt-mark">' + AVATAR + '</span>' +
            '<span class="fsai-opt-body">' +
              '<span class="fsai-opt-t">Ask Julie</span>' +
              '<span class="fsai-opt-d">Bouquet ideas, prices, delivery and flower care — answered straight away.</span>' +
              '<span class="fsai-opt-status">' +
                '<span class="fsai-status-dot"></span>Available now' +
              '</span>' +
            '</span>' +
          '</button>' +
          '<button class="fsai-opt fsai-opt--live" type="button">' +
            '<span class="fsai-opt-mark fsai-opt-mark--live">' + ICON_AGENT + '</span>' +
            '<span class="fsai-opt-body">' +
              '<span class="fsai-opt-t">Fleur</span>' +
              '<span class="fsai-opt-d">Live chat support — talk to one of our florists about an order, a delivery or something specific.</span>' +
              '<span class="fsai-opt-status fsai-live-status">' +
                '<span class="fsai-status-dot"></span><span class="fsai-live-status-text"></span>' +
              '</span>' +
            '</span>' +
          '</button>' +
          /* only shown to a signed-out shopper - see wireSignIn() */
          '<a class="fsai-signin" href="#" hidden>' +
            '<span class="fsai-signin-icon">' + ICON_ORDER + '</span>' +
            '<span class="fsai-signin-text">Sign in to track your orders</span>' +
            '<span class="fsai-signin-go">' + ICON_ARROW + '</span>' +
          '</a>' +
        '</div>' +

        /* view 2 - Julie */
        '<div class="fsai-view fsai-chatview" hidden>' +
          '<div class="fsai-log" role="log" aria-live="polite" aria-relevant="additions" tabindex="0">' +
            '<div class="fsai-intro">' +
              '<span class="fsai-intro-title">Hello, I’m Julie.</span>' +
              '<span class="fsai-intro-body">Tell me who the flowers are for and roughly what you’d like to spend, and I’ll pick a few from what we’ve got in the shop today. Happy to help with delivery and flower care too.</span>' +
            '</div>' +
          '</div>' +
          /* Also offered here, not only on the chooser: someone deep in a
             conversation who wants to track an order should not have to
             navigate back out to find it. Signed-out shoppers only. */
          '<a class="fsai-signin fsai-signin--bar" href="#" hidden>' +
            '<span class="fsai-signin-icon">' + ICON_ORDER + '</span>' +
            '<span class="fsai-signin-text">Sign in to track your orders</span>' +
            '<span class="fsai-signin-go">' + ICON_ARROW + '</span>' +
          '</a>' +
          '<form class="fsai-composer">' +
            '<input class="fsai-input" type="text" autocomplete="off" autocapitalize="sentences" ' +
                   'spellcheck="false" maxlength="500" aria-label="Your message to Julie" ' +
                   'placeholder="Ask Julie anything…" />' +
            '<button class="fsai-send" type="submit" aria-label="Send message">' + ICON_SEND + '</button>' +
          '</form>' +
        '</div>' +


        '<div class="fsai-foot">' +
          '<a class="fsai-foot-call" href="#"></a>' +
          '<span class="fsai-foot-ai">' +
            '<span class="fsai-foot-sep">·</span>' +
            'Julie is Flower Station’s AI assistant · confirm prices and delivery at checkout' +
          '</span>' +
        '</div>' +
      '</div>' +
    '</div>';

  /* ------------------------------------------------------------- utilities */

  /* Only ever put an http(s) URL or a same-origin path into an href.
     The payload comes from our own backend, but a `javascript:` URL in an
     href is an XSS vector that no amount of textContent discipline catches. */
  function safeHref(raw) {
    if (typeof raw !== 'string') return null;
    var url = raw.trim();
    if (!url) return null;
    if (/^\/[^/]/.test(url) || url === '/') return url;      // storefront path
    if (/^https?:\/\//i.test(url)) return url;
    /* Dialling and writing an email are the two things a customer with a
       problem most wants to do, and a card offering either was arriving with
       nothing behind it. Nothing else is added: javascript:, data: and blob:
       stay refused, which is what this function is for. */
    if (/^tel:[+\d][\d\s()-]*$/i.test(url)) return url;
    if (/^mailto:[^@\s]+@[^@\s]+$/i.test(url)) return url;
    return null;
  }

  /* The readable half of a tel: or mailto: link, and empty for anything
     else - which is how the caller tells a contact card from an ordinary one. */
  /* The shop's number, however a sentence happens to spell it, restated the
     way data-phone spells it. A run of digits that is not ours is untouched. */
  function ourNumber(text) {
    var out = String(text == null ? '' : text);
    var mine = String(PHONE || '').replace(/[^0-9+]/g, '');
    if (!mine) return out;
    /* The same number written nationally: +44 20 ... and 020 ... are one
       number, and the assistant may send either. */
    var national = mine;
    if (DIAL_CODE && mine.indexOf(DIAL_CODE) === 0) {
      national = '0' + mine.slice(DIAL_CODE.length);
    }
    /* Groups of digits joined by at most a couple of spaces, brackets or
       hyphens. Deliberately not one loose character class: that was greedy
       enough to swallow the " - 9" out of "020 7724 7525 - 9am to 6pm" and
       then match nothing. A separator of three characters ends the number,
       which is what a dash between clauses looks like. */
    return out.replace(/\+?\d+(?:[\s()-]{1,2}\d+)*/g, function (run) {
      var digits = run.replace(/[^0-9+]/g, '');
      return (digits === mine || digits === national) ? PHONE : run;
    });
  }

  function contactDetail(url) {
    if (typeof url !== 'string') return '';
    if (/^mailto:/i.test(url)) return url.slice(7);
    /* Deliberately not the digits out of the link. The assistant sends its
       own copy of the number and has no idea how the shop writes it; this is
       the one the footer, the call screen and the wait screen all show. */
    if (/^tel:/i.test(url)) return PHONE;
    return '';
  }

  /* Only a page belongs in a new tab. Handing a phone number or an email
     address to a fresh window leaves the customer staring at a blank one. */
  function opensInNewTab(url) {
    return /^https?:\/\//i.test(url) || /^\//.test(url);
  }

  function safeImageSrc(raw) {
    if (typeof raw !== 'string') return null;
    var url = raw.trim();
    if (!url) return null;
    if (/^https?:\/\//i.test(url) || /^\/\//.test(url)) return url;
    if (/^\/[^/]/.test(url)) return url;
    return null;
  }

  function isNum(v) { return typeof v === 'number' && isFinite(v); }
  function money(n) { return '£' + n.toFixed(2); }

  /* price_max equals price_min when the product has a single variant, so
     "from" is only honest when the range is real. */
  function priceLabel(card) {
    if (!isNum(card.price_min)) return null;
    if (isNum(card.price_max) && card.price_max - card.price_min > 0.005) {
      return 'from ' + money(card.price_min);
    }
    return money(card.price_min);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== null && text !== undefined) node.textContent = String(text);
    return node;
  }

  function str(v) { return typeof v === 'string' ? v.trim() : ''; }
  function arr(v) { return Array.isArray(v) ? v : []; }

  /* =====================================================================
     Theme bridge - the storefront's own cart plumbing
     ---------------------------------------------------------------------
     Only the DOM is isolated by the shadow root; `window` is shared, so the
     theme's globals are reachable. Everything below is guarded so a theme
     update that renames or drops one of them degrades the Add to cart
     button instead of breaking the chat.

     One trap worth spelling out: constants.js declares PUB_SUB_EVENTS with
     `const`, which does NOT create a property on `window` - it only binds
     in the shared global lexical scope. So it has to be read as a BARE
     identifier behind `typeof`; `window.PUB_SUB_EVENTS` is undefined.
     `publish` and `fetchConfig` are function declarations, so those do land
     on window, and `routes` is assigned explicitly in theme.liquid.

     Load order is safe: constants.js / pubsub.js / global.js are deferred in
     <head> (theme.liquid:70-72) and this file is deferred at the end of
     <body>, so they have all executed by the time anything here runs.
     ===================================================================== */

  function themeEvents() {
    try {
      return (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS) ? PUB_SUB_EVENTS : null;
    } catch (e) { return null; }
  }

  function themePublish(key, payload) {
    var events = themeEvents();
    if (!events || !events[key]) return false;
    try {
      if (typeof publish !== 'function') return false;
      publish(events[key], payload);
      return true;
    } catch (e) { return false; }
  }

  function themeFetchConfig() {
    try {
      if (typeof fetchConfig === 'function') {
        var cfg = fetchConfig('javascript');
        if (cfg && typeof cfg === 'object') return cfg;
      }
    } catch (e) { /* fall through */ }
    return { method: 'POST', headers: { Accept: 'application/javascript' } };
  }

  /* Shopify's locale/market root ("/" or "/en-gb/"). Using it keeps every
     storefront request on the CURRENT origin and inside the right locale. */
  function shopifyRoot() {
    try {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      if (typeof root === 'string' && root) return root;
    } catch (e) { /* ignore */ }
    return '/';
  }

  /* The current path without the market prefix: "/en-us/cart" -> "/cart". */
  function pagePath() {
    var path = window.location.pathname || '/';
    var root = shopifyRoot();
    if (root !== '/' && path.indexOf(root) === 0) path = '/' + path.slice(root.length);
    return path.replace(/\/+$/, '') || '/';
  }

  function helloHiddenHere() {
    var path = pagePath();
    return HELLO_HIDE_ON.some(function (hidden) {
      return path === hidden || path.indexOf(hidden + '/') === 0;
    });
  }

  function cartAddUrl() {
    try {
      var url = window.routes && window.routes.cart_add_url;
      if (typeof url === 'string' && url) return url;
    } catch (e) { /* ignore */ }
    return shopifyRoot() + 'cart/add';
  }

  function themeCart() {
    try {
      return document.querySelector('cart-notification') ||
             document.querySelector('cart-drawer');
    } catch (e) { return null; }
  }

  /* ---------------------------------------------------------------------
     addon-redirect.js is loaded on EVERY page (theme.liquid:74) and it
     monkey-patches window.fetch:

         if (typeof url === "string" && url.includes("/cart/add")) {
             ...
             window.location.href = "/pages/product-addons?for=..."
             return new Promise(() => {})          // never resolves
         }

     For a main product (a bouquet is one) that means the whole page
     NAVIGATES to the add-ons upsell page and the promise never settles - the
     spinner would spin for ever and the customer would be thrown out of the
     conversation, which this widget must never do.

     Passing a Request object makes the interceptor's own `typeof url ===
     "string"` guard false, so it hands the response straight back. The
     theme's real product forms still pass a string URL and still get their
     upsell flow - nothing about the storefront's behaviour changes.
     --------------------------------------------------------------------- */
  function cartAddTarget(config) {
    var url = cartAddUrl();
    try {
      if (typeof window.Request === 'function') return new window.Request(url, config);
    } catch (e) { /* fall through */ }
    return url;
  }

  /* The card url is ABSOLUTE (https://flowerstation.co.uk/...). On a
     *.myshopify.com preview that is a different origin: fetching it would be
     blocked by CORS, and adding to it would fill a different shop's cart. So
     keep only the handle and rebuild the path on the current origin. */
  function productJsUrl(cardUrl) {
    var match = /\/products\/([^/?#]+)/.exec(String(cardUrl || ''));
    if (!match) return null;
    return shopifyRoot() + 'products/' + match[1] + '.js';
  }

  /* Normalise /products/{handle}.js variants. Prices there are in PENCE -
     6500 means £65.00 - and the cart API only accepts NUMERIC variant ids. */
  function readVariants(payload) {
    var raw = payload && payload.variants;
    if (!Array.isArray(raw)) return null;
    var out = [];
    raw.forEach(function (v) {
      if (!v || v.id === undefined || v.id === null) return;
      var id = String(v.id);
      if (!/^[0-9]+$/.test(id)) return;
      out.push({
        id: id,
        title: str(v.title) || 'Default',
        price: (typeof v.price === 'number' && isFinite(v.price)) ? v.price / 100 : null,
        available: v.available !== false
      });
    });
    return out.length ? out : null;
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    try {
      build();
    } catch (err) {
      // A silent failure in here is impossible to diagnose from outside.
      console.error('[Flower Station] chat widget failed to start:', err);
    }
  }

  function build() {
    if (!document.body) return;
    if (!API) {
      console.warn('[Flower Station] chat widget: data-api is missing, not rendering.');
      return;
    }
    if (!document.body.attachShadow && !Element.prototype.attachShadow) return;

    var host = document.createElement('div');
    host.className = 'fsai-host';

    /* Every property !important. The host lives in the storefront's DOM where
       the theme's CSS competes with it, and a theme rule setting display:none
       on an injected container has hidden this widget before. transform/filter
       are pinned to none so the fixed positioning inside cannot be captured by
       a new containing block. */
    /* bottom, not top - see the note beside releaseVisualViewport(). A
       top-anchored layer of svh height stops short of the screen whenever
       the address bar is hidden, and takes the launcher up with it. */
    var hostCss = {
      'display': 'block', 'position': 'fixed', 'top': 'auto', 'left': '0',
      'right': 'auto', 'bottom': '0',
      'width': '100%', 'height': '100%',
      'margin': '0', 'padding': '0', 'border': '0',
      'visibility': 'visible', 'opacity': '1',
      'pointer-events': 'none',
      'transform': 'none', 'filter': 'none', 'perspective': 'none',
      'clip-path': 'none', 'contain': 'none', 'isolation': 'auto',
      'max-width': 'none', 'max-height': 'none', 'min-width': '0', 'min-height': '0',
      'z-index': '2147483000'
    };
    Object.keys(hostCss).forEach(function (prop) {
      host.style.setProperty(prop, hostCss[prop], 'important');
    });

    /* height:100% above resolves against the large viewport on a phone, so
       the bottom of this layer hides behind the address bar and shifts as the
       bar comes and goes. svh is the SMALL viewport - the one with the chrome
       showing - which is the only height that never changes while scrolling.
       A browser that does not know svh ignores this and keeps the 100%. */
    host.style.setProperty('height', '100svh', 'important');
    host.style.setProperty('--fsai-offset-side', OFFSET_SIDE);

    document.body.appendChild(host);

    var root = host.attachShadow({ mode: 'open' });

    /* OPTION 1, deliberately: the widget stays invisible until its stylesheet
       has arrived, then appears. The alternative was letting it paint
       unstyled for a frame, and an unstyled launcher sitting on the
       storefront is far more noticeable than one that appears 40ms later.

       If the stylesheet cannot be fetched at all we reveal anyway: a plain
       widget still works, an invisible one does not. */
    if (CSS_URL) {
      var sheet = document.createElement('link');
      sheet.setAttribute('rel', 'stylesheet');
      sheet.setAttribute('href', CSS_URL);
      var reveal = function () {
        host.style.setProperty('visibility', 'visible', 'important');
      };
      host.style.setProperty('visibility', 'hidden', 'important');
      sheet.addEventListener('load', reveal);
      sheet.addEventListener('error', reveal);
      /* Belt and braces: a cached stylesheet can fire before the listener is
         attached in some browsers, and nothing should be able to leave the
         widget hidden for good. */
      window.setTimeout(reveal, 3000);
      root.appendChild(sheet);
    }

    var holder = document.createElement('div');
    holder.innerHTML = SHELL;
    while (holder.firstChild) root.appendChild(holder.firstChild);

    // data-motion="always" - see the note beside MOTION
    if (MOTION === 'always') {
      var layerEl = root.querySelector('.fsai-layer');
      if (layerEl) layerEl.classList.add('fsai-motion-always');
    }

    /* The stylesheet hugs the right edge. Opting into the left one just swaps
       which physical offset is set - no logical properties, so there is no
       chance of the shop's own `dir` attribute flipping the widget. */
    if (SIDE === 'left') {
      root.querySelectorAll('.fsai-launcher, .fsai-panel').forEach(function (node) {
        node.style.setProperty('right', 'auto');
        node.style.setProperty('left', 'var(--fsai-offset-side, 18px)');
      });
    }

    init(root);
  }

  /* ------------------------------------------------------------------- app */

  function init(root) {
    var launcher = root.querySelector('.fsai-launcher');
    var panel = root.querySelector('.fsai-panel');
    var closeBtn = root.querySelector('.fsai-close');
    var log = root.querySelector('.fsai-log');
    var form = root.querySelector('.fsai-composer');
    var input = root.querySelector('.fsai-input');
    var sendBtn = root.querySelector('.fsai-send');

    var sessionId = readStore(SESSION_KEY) || null;

    /* ---- handing this conversation to a person ------------------------
       The id exists only once Julie has actually been spoken to - it is
       written when she first answers - so having one IS having talked to
       her. That makes the question the live side needs to ask a simple
       one, and it does not matter how they reached live support: the card,
       the chooser, or the four-option screen out of hours. */
    function botSessionId() { return sessionId || ''; }
    var busy = false;
    var historyRequested = false;
    var isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

    /* ---- feature modules ---------------------------------------------
       Live support is a separate file (assets/fsai-livechat.js) and attaches
       itself here. Everything below asks for it through mod(), which returns
       undefined when it has not loaded - so Julie works on her own and a
       missing or broken livechat file costs live chat and nothing else.

       One module, not a list: there is one, and a list would invite the kind
       of indirection nobody can follow six months later. */
    /* Filled in by feature modules through FSAI.cardAction(). Empty means an
       action card from the assistant renders as plain text rather than a
       button that does nothing. */
    var CARD_ACTIONS = {};

    /* The theme's own "Set a reminder" button, pressed directly rather than
       navigating to ?reminders=open and waiting for the page to come back.
       Only some pages render that section, so the link is still the
       fallback - and reminder.js honours the query string on arrival.

       The chat is NOT closed: the modal adds body.overflow-hidden, which
       hides this widget while it is up (see shopOwnsScreen), so the
       conversation is waiting exactly as they left it afterwards. */
    CARD_ACTIONS.open_reminders = function () {
      var trigger = null;
      try {
        trigger = document.getElementById('openRemPopup') ||
                  document.getElementById('openReminderPopup');
      } catch (e) { trigger = null; }
      if (trigger) { trigger.click(); return; }
      /* #openRemPopup, not ?reminders=open: the hash is what reminder.js
         actually listens for (checkHashAndOpen). The query-string form was
         only ever handled by a change that is no longer in the theme, so a
         link to it lands on the homepage and does nothing. */
      try {
        window.location.href = shopifyRoot() + '#openRemPopup';
      } catch (e) { /* nothing else to try */ }
    };

    var MOD = null;
    var EXTRA_VIEWS = [];        // views a module added: { name, node }
    var QUIET_VIEWS = [];        // ...of those, the ones that hide the footer

    function mod(name, a, b) {
      if (!MOD || typeof MOD[name] !== 'function') return undefined;
      try { return MOD[name](a, b); }
      catch (e) {
        /* A module throwing must not take the assistant down with it. */
        if (window.console && console.error) console.error('[Flower Station] live support:', e);
        return undefined;
      }
    }

    /* While an answer is being written, the reader stays where they are - see
       holdTranscript(). Everything else scrolls as it always did. */
    var scrollHeld = false;

    function scrollToEnd() {
      if (scrollHeld) return;
      log.scrollTop = log.scrollHeight;
    }

    function holdTranscript(on) { scrollHeld = !!on; }

    /* Bring one element inside the transcript into view with the SMALLEST
       possible scroll, and only if it is actually off screen.

       Add to cart must never call scrollToEnd(): a customer who has scrolled
       back up to an earlier card and taps Add would be thrown down to the
       newest message, away from the very card they are using. Measured with
       getBoundingClientRect deltas rather than offsetTop, because .fsai-log is
       not a positioned ancestor so offsetTop would be relative to the panel. */
    /* Put the TOP of a new answer near the top of the transcript and leave it
       there. Following the bottom as tokens arrived meant the text slid upward
       while it was being read, and a long answer left the reader stranded at
       the end of something they had not started. */
    function pinToStart(node) {
      if (!node) return;
      try {
        var box = node.getBoundingClientRect();
        var view = log.getBoundingClientRect();
        if (!box.height && !box.width) return;    // not laid out yet
        log.scrollTop += (box.top - view.top) - 10;
      } catch (e) { /* no layout available */ }
    }

    function revealInLog(node) {
      if (!node) return;
      try {
        var box = node.getBoundingClientRect();
        var view = log.getBoundingClientRect();
        if (!box.height && !box.width) return;      // not laid out yet
        var pad = 12;
        if (box.bottom > view.bottom) {
          log.scrollTop += (box.bottom - view.bottom) + pad;
        } else if (box.top < view.top) {
          log.scrollTop -= (view.top - box.top) + pad;
        }
      } catch (e) { /* no layout available */ }
    }

    /* ------------------------------------------------- staying out of the way
       The storefront floats its own controls in this same right-hand column -
       a green CALL NOW anchor and the Brevo bubble - and the theme's own script
       shuffles CALL NOW between bottom:10px and bottom:100px depending on
       whether Brevo is showing. A hard-coded offset would be wrong half the
       time, so measure the column and sit above it.

       Read-only: this looks at getBoundingClientRect() and never touches those
       elements. Anything taller than STACK_MAX_ITEM is an expanded chat panel
       rather than a button, and is ignored - otherwise opening Brevo would
       fling this launcher to the top of the screen. */
    function measureStack() {
      if (SIDE !== 'right') return;          // only the right column is shared
      var floor = STACK_FLOOR;
      var viewportH = window.innerHeight || 0;
      var viewportW = window.innerWidth || 0;

      STACK_SELECTORS.forEach(function (sel) {
        var node;
        try { node = document.querySelector(sel); } catch (e) { return; }
        if (!node) return;
        var box = node.getBoundingClientRect();
        if (!box.height || box.height > STACK_MAX_ITEM) return;
        if (box.bottom < viewportH * 0.4) return;        // not bottom-anchored
        if (box.right < viewportW - 220) return;         // not in our column
        var topFromBottom = viewportH - box.top;         // how far up it reaches
        if (topFromBottom + STACK_GAP > floor) floor = topFromBottom + STACK_GAP;
      });

      // Never push the launcher more than halfway up the viewport.
      floor = Math.min(floor, Math.max(STACK_FLOOR, viewportH * 0.5));
      floor = Math.round(floor);

      /* A phone hiding its address bar mid-scroll produces a reading that is
         wrong by the height of that bar for a frame, and the frame after it
         puts the launcher back. Refusing to act on small differences makes
         that invisible, and still honours a button actually appearing in
         this column - which moves the floor by far more than this. */
      if (appliedFloor !== null && Math.abs(floor - appliedFloor) < STACK_HYSTERESIS) return;
      appliedFloor = floor;

      launcher.style.setProperty('bottom', floor + 'px');
      // keep her greeting level with her, wherever the column pushed her
      var bubble = root.querySelector('.fsai-hello');
      if (bubble) bubble.style.setProperty('bottom', (floor + 12) + 'px');
    }

    /* The last size we actually measured at. A phone hiding its address bar
       fires resize with the WIDTH unchanged and the height a few dozen pixels
       different; that is not a layout change and must not move the launcher.
       A rotation or a real window resize changes the width, or the height by
       a lot, and both still go through. */
    var measuredW = 0;
    var measuredH = 0;
    /* The bottom currently written on the launcher, so a recomputed value
       can be compared against what the customer is actually looking at. */
    var appliedFloor = null;

    function worthMeasuring() {
      var w = window.innerWidth || 0;
      var h = window.innerHeight || 0;
      if (w !== measuredW) { measuredW = w; measuredH = h; return true; }
      /* 150px is comfortably more than any mobile address bar and well under
         a window resize somebody meant. */
      if (Math.abs(h - measuredH) >= 150) { measuredH = h; return true; }
      return false;
    }

    var measureQueued = false;
    function remeasure(force) {
      if (force !== true && !worthMeasuring()) return;
      if (measureQueued) return;
      measureQueued = true;
      var run = function () { measureQueued = false; try { measureStack(); } catch (e) {} };
      if (window.requestAnimationFrame) window.requestAnimationFrame(run);
      else window.setTimeout(run, 16);
    }

    /* ------------------------------------------- stepping aside for the shop
       This widget deliberately sits above everything (z-index 2147483000) so
       that hostile theme CSS cannot bury it. The cost is that it would also
       float on top of the storefront's own overlays, which top out at 9999:

         - the cart drawer, the details modals and the mobile menu, which lock
           the page with body.overflow-hidden (cart-drawer.js:47,
           details-modal.js:31, global.js:635)
         - .reminder-overlay (z 9000) and .bc-rpop-overlay (z 9500), both
           toggled by an inline style.display

       So while the shop owns the screen, hide the widget entirely and put it
       back - panel state intact - once the overlay closes. Read-only: this
       observes those elements and never modifies them. */
    var hostEl = root.host;
    var OVERLAYS = ['.reminder-overlay', '.bc-rpop-overlay'];
    var watched = [];

    function shopOwnsScreen() {
      var body = document.body;
      if (!body) return false;
      var cls = typeof body.className === 'string' ? body.className : '';
      if (/(^|\s)overflow-hidden(-[\w]+)?(\s|$)/.test(cls)) return true;
      for (var i = 0; i < OVERLAYS.length; i++) {
        var node;
        try { node = document.querySelector(OVERLAYS[i]); } catch (e) { continue; }
        if (!node) continue;
        var display = '';
        try { display = window.getComputedStyle(node).display; } catch (e) { continue; }
        if (display && display !== 'none') return true;
      }
      return false;
    }

    function syncVisibility() {
      hostEl.style.setProperty(
        'display', shopOwnsScreen() ? 'none' : 'block', 'important');
    }

    var overlayWatcher = window.MutationObserver
      ? new window.MutationObserver(syncVisibility) : null;

    /* Those overlays are section markup, so they normally exist by now - but
       re-bind on the staggered ticks below in case one is injected late. */
    function bindOverlayWatchers() {
      if (!overlayWatcher) return;
      OVERLAYS.forEach(function (sel) {
        var node;
        try { node = document.querySelector(sel); } catch (e) { return; }
        if (!node || watched.indexOf(node) !== -1) return;
        watched.push(node);
        overlayWatcher.observe(node, { attributes: true, attributeFilter: ['style', 'class'] });
      });
    }

    if (overlayWatcher) {
      // One node, one attribute - the theme already runs a body-wide observer.
      overlayWatcher.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    function syncChrome() { remeasure(true); syncVisibility(); bindOverlayWatchers(); }

    syncChrome();
    window.addEventListener('resize', remeasure);
    /* Brevo loads on window `load` and injects its bubble asynchronously, and
       the theme re-positions CALL NOW ~2s later. Re-measure across that window
       rather than observing the whole body, which the theme already does. */
    window.addEventListener('load', syncChrome);
    [400, 1200, 2500, 5000].forEach(function (ms) { window.setTimeout(syncChrome, ms); });

    function setBusy(state) {
      busy = state;
      input.disabled = state;
      sendBtn.disabled = state;
      root.querySelectorAll('.fsai-chip').forEach(function (chip) {
        chip.disabled = state;
      });
    }

    /* ===================================================================
       Views: choose -> julie | call
       -------------------------------------------------------------------
       The customer picks who to talk to; nothing escalates on its own. Live
       support is only offered inside staffed hours, and until the live
       channel exists that choice leads to the shop's number rather than to
       a chat nobody is watching.
       =================================================================== */
    var viewChoose = root.querySelector('.fsai-choose');
    var viewChat = root.querySelector('.fsai-chatview');
    var viewCall = root.querySelector('.fsai-call');
    var viewEnquiry = root.querySelector('.fsai-enquiry');
    var viewLive = root.querySelector('.fsai-live');
    var backBtn = root.querySelector('.fsai-back');
    var headTitle = root.querySelector('.fsai-head-title');
    var headSub = root.querySelector('.fsai-head-sub');
    var headMark = root.querySelector('.fsai-head-mark');
    var footAi = root.querySelector('.fsai-foot-ai');
    var foot = root.querySelector('.fsai-foot');
    var MODE_KEY = 'fsai_mode';
    var view = 'choose';

    /* ---- the sentence they were half-way through -------------------------
       Saved on any navigation away, not only the sign-in trip: clicking a
       storefront link mid-message loses it just as surely. Restored when the
       panel next opens into her conversation, then cleared, so it never
       reappears days later next to a stale transcript. */
    function saveDraft() {
      var text = input.value.trim();
      if (text) writeStore(DRAFT_KEY, text.slice(0, 500));
      else clearStore(DRAFT_KEY);
    }

    function restoreDraft() {
      var text = readStore(DRAFT_KEY);
      clearStore(DRAFT_KEY);
      if (text && !input.value) input.value = text;
    }

    /* pagehide, not beforeunload: it fires on mobile Safari's back-forward
       cache path too, where beforeunload does not. */
    window.addEventListener('pagehide', function () {
      saveDraft();
      /* The module owns its sockets and timers, including the polling one
         that must stop when the page is put away - bfcache keeps intervals
         alive. */
      mod('onPageHide');
    });

    /* ---- signing in ------------------------------------------------------
       Shopify owns the login page; the widget only offers the door and makes
       sure the shopper lands back where they were with the chat still open. */
    (function wireSignIn() {
      var rows = root.querySelectorAll('.fsai-signin');
      if (!rows.length) return;

      /* A SAME-ORIGIN PATH ONLY - deliberately stricter than safeHref(), which
         allows absolute https:// links because product cards need them. A login
         link that could point off-site is a phishing page waiting to happen,
         so anything but a path on this shop is refused. */
      var href = /^\/[^/]/.test(String(LOGIN_URL || '')) ? LOGIN_URL : null;

      rows.forEach(function (row) {
        if (SIGNED_IN || !href) { row.remove(); return; }
        row.href = href;
        row.addEventListener('click', function () {
          /* They are about to leave. Mark the trip so the widget reopens on
             the way back even on a phone, where it otherwise stays shut. */
          writeStore(OPEN_KEY, '1');
          writeStore(RETURNING_KEY, '1');
          saveDraft();
        });
        row.hidden = false;
      });
    })();

    /* Greet a signed-in shopper by name. Their name is only ever written with
       textContent - it arrives through the browser and is treated as text. */
    (function personalise() {
      var name = firstName();
      if (!name) return;

      var helloName = root.querySelector('.fsai-hello-name');
      if (helloName) helloName.textContent = 'Hi ' + name + ' — need a hand?';

      var introTitle = root.querySelector('.fsai-intro-title');
      if (introTitle) introTitle.textContent = 'Hello ' + name + ', I’m Julie.';

      var chooseTitle = root.querySelector('.fsai-choose-title');
      if (chooseTitle) chooseTitle.textContent = 'Hello ' + name;
    })();

    /* the number, everywhere it appears */
    (function wirePhone() {
      /* Only the footer. The call screen's own button is wired by whoever
         adds that screen - it does not exist yet when this runs. */
      var foot = root.querySelector('.fsai-foot-call');
      if (foot) { foot.href = telHref(); foot.textContent = 'Call ' + PHONE; }
    })();

    /* Julie's portrait over her conversation, the shop's logo over live
       support, nothing where neither applies. */
    function setHeadMark(kind) {
      if (!headMark) return;
      if (kind === 'team' && !AGENT_AVATAR_URL) kind = 'none';
      headMark.hidden = kind === 'none';
      if (kind === 'none') return;
      while (headMark.firstChild) headMark.removeChild(headMark.firstChild);
      headMark.className = 'fsai-head-mark' +
        (kind === 'team' ? ' fsai-head-mark--photo' : '');
      if (kind === 'team') { mod('paintAgentMark', headMark); return; }
      headMark.innerHTML = AVATAR;   // authored constant, never API data
    }

    function setHead(title, sub, showMark) {
      headTitle.textContent = title;
      // rebuild the sub line: a live dot only when something really is live
      while (headSub.firstChild) headSub.removeChild(headSub.firstChild);
      if (showMark) headSub.appendChild(el('span', 'fsai-dot-live'));
      headSub.appendChild(document.createTextNode(sub));
      headMark.hidden = !showMark;
    }

    function showView(next) {
      view = next;
      viewChoose.hidden = next !== 'choose';
      viewChat.hidden = next !== 'julie';
      /* Views a module added hide themselves by name. */
      for (var vi = 0; vi < EXTRA_VIEWS.length; vi++) {
        EXTRA_VIEWS[vi].node.hidden = next !== EXTRA_VIEWS[vi].name;
      }
      backBtn.hidden = next === 'choose';
      footAi.hidden = next !== 'julie';
      /* The call screen already leads with the number and offers "Ask Julie
         instead", so the footer was saying both a second time. The enquiry
         screen wants the same quiet: one thing is being asked for. */
      foot.hidden = QUIET_VIEWS.indexOf(next) !== -1;

      /* Deliberately no teardown here any more. Leaving the chat view is not
         leaving the conversation: a reply that lands while they are reading
         the chooser, or asking Julie something, still has to reach them. The
         socket ends with the conversation - on unload, on ending it, or when
         the service says it is gone - not with the view.

         The belief this replaces, that an open socket held somebody in the
         agent's queue, was never true: position comes from the conversation
         being in `waiting`, and the socket's teardown touches neither. */
      if (next === 'julie') {
        setHead('Julie', 'Flower Station florist', true);
        setHeadMark('julie');
        try { writeStore(MODE_KEY, 'julie'); } catch (e) { /* ignore */ }
        restoreDraft();
        restoreHistory();
        scrollToEnd();
        if (!isTouch) input.focus();
      } else if (next === 'choose') {
        setHead('Flower Station', 'How can we help?', false);
        clearStore(MODE_KEY);
        mod('paintStatus');
      } else {
        /* One of the module's own views: it knows what its header says. */
        mod('onView', next);
      }
    }


    /* -------------------------------------------------------- panel state */

    /* ---- the on-screen keyboard --------------------------------------
       See the note at the top of this file about 100svh. Tracked only while
       the panel is open: the visual viewport also moves when the address bar
       hides, and following that with the panel shut is what used to make the
       launcher drift up and down the screen. */
    var vv = window.visualViewport || null;

    /* While the panel is open the layer is pinned to where the VISIBLE
       viewport starts, so the keyboard cannot be drawn over the composer.
       That needs a top, so the bottom is released. */
    function fitToVisualViewport() {
      if (!vv) return;
      /* Round down: a fractional height leaves a hairline of storefront
         showing under the composer on some devices. */
      hostEl.style.setProperty('height', Math.floor(vv.height) + 'px', 'important');
      hostEl.style.setProperty('top', Math.round(vv.offsetTop || 0) + 'px', 'important');
      hostEl.style.setProperty('bottom', 'auto', 'important');
    }

    /* Back to hanging from the bottom edge, which is the only anchor that
       does not move while the address bar comes and goes. A top-anchored
       layer of svh height is what used to walk the launcher up the screen
       on every scroll. */
    function releaseVisualViewport() {
      if (!vv) return;
      hostEl.style.setProperty('height', '100svh', 'important');
      hostEl.style.setProperty('top', 'auto', 'important');
      hostEl.style.setProperty('bottom', '0', 'important');
    }

    function watchViewport(on) {
      if (!vv) return;
      if (on) {
        vv.addEventListener('resize', fitToVisualViewport);
        vv.addEventListener('scroll', fitToVisualViewport);
        fitToVisualViewport();
      } else {
        vv.removeEventListener('resize', fitToVisualViewport);
        vv.removeEventListener('scroll', fitToVisualViewport);
        releaseVisualViewport();
      }
    }

    function open() {
      hideHello();
      panel.hidden = false;
      watchViewport(true);
      /* Live availability now updates in front of them rather than going
         stale while they read the card. */
      mod('openStatusSocket');
      launcher.hidden = true;
      launcher.setAttribute('aria-expanded', 'true');
      writeStore(OPEN_KEY, '1');
      /* Someone mid-conversation comes back to it; everyone else gets the
         choice. A live chat only resumes if we still hold its id. */
      var was = readStore(MODE_KEY);
      if (was === 'julie') showView('julie');
      else if (was === 'live' && mod('hasConversation')) mod('enterLive');
      else showView('choose');
    }

    function close() {
      /* The live socket deliberately STAYS. It is what lets the badge appear
         on the launcher while they carry on shopping; see the note above
         noteUnread(). It ends on page unload like it always did.

         The status socket does go: it only keeps the availability line on the
         chooser card fresh, and nothing is reading that card while the panel
         is shut. */
      mod('closeStatusSocket');
      panel.hidden = true;
      launcher.hidden = false;
      launcher.setAttribute('aria-expanded', 'false');
      /* Below panel.hidden deliberately: split.mjs rewires the two lines
         above as a pair, and splitting them made it miss. */
      watchViewport(false);
      clearStore(OPEN_KEY);
      launcher.focus();
    }

    /* ---- what a feature module may use ---------------------------------
       Deliberately explicit. A module reaching into Julie's internals would
       make every future change to this file a guess about what it broke. */
    var SHARED = {
      version: 1,
      root: root, panel: panel, launcher: launcher, backBtn: backBtn,

      el: el, str: str, arr: arr, isTouch: isTouch,
      safeHref: safeHref, safeImageUrl: safeImageUrl, looksLikeEmail: looksLikeEmail,
      clockFromIso: clockFromIso, whenFromIso: whenFromIso,
      telHref: telHref, dialable: dialable, ourNumber: ourNumber,
      visitorIdentity: visitorIdentity, currentOwner: currentOwner,
      guardPreviousCustomer: guardPreviousCustomer, liveSocketUrl: liveSocketUrl,
      readStore: readStore, writeStore: writeStore, clearStore: clearStore,
      /* Both Julie's: the id of the conversation the customer has been
         having with her, and her portrait, which live support needs once
         her turns appear in its transcript after a handover. */
      botSessionId: botSessionId, julieAvatar: avatar,

      API: API, LIVE_API: LIVE_API, LIVE_ENABLED: LIVE_ENABLED,
      PHONE: PHONE, EMAIL: EMAIL, CUSTOMER_EMAIL: CUSTOMER_EMAIL,
      LIVE_MAX_CHARS: LIVE_MAX_CHARS,
      keys: {
        LIVE_ID: LIVE_ID_KEY, LIVE_SEEN: LIVE_SEEN_KEY, LIVE_MAIL: LIVE_MAIL_KEY,
        LIVE_OWNER: LIVE_OWNER_KEY, LIVE_AT: LIVE_AT_KEY, MODE: MODE_KEY
      },

      icons: {
        PHONE: ICON_PHONE, MAIL: ICON_MAIL, ARROW: ICON_ARROW,
        AGENT: ICON_AGENT, SEND: ICON_SEND, BACK: ICON_BACK,
        CLOSE: ICON_CLOSE, CLIP: ICON_CLIP
      },
      AGENT_AVATAR: AGENT_AVATAR, AGENT_AVATAR_URL: AGENT_AVATAR_URL,

      showView: showView, setHead: setHead, setHeadMark: setHeadMark,
      currentView: function () { return view; },
      hideLauncherBadgeHost: launcher,

      /* Add markup and register a view name in one go. quiet:true means the
         footer steps aside while that view is up. */
      addView: function (name, node, quiet) {
        EXTRA_VIEWS.push({ name: name, node: node });
        if (quiet) QUIET_VIEWS.push(name);
      },
      /* What a card from the assistant is allowed to ask for. */
      cardAction: function (name, fn) { CARD_ACTIONS[name] = fn; },

      /* Attach. Called once, by assets/fsai-livechat.js. */
      register: function (api) {
        MOD = api;
        var card = root.querySelector('.fsai-opt--live');
        if (card) card.hidden = false;
        mod('boot');
      }
    };

    /* Before the namespace is published, because publishing it flushes
       onReady - and that is where fsai-livechat.js attaches and unhides
       this card. Hiding it after the flush undid the reveal every time,
       which left a new customer with no way to reach live support at all. */
    /* No live-support module, no live-support card: better an honest chooser
       with one option than a card that does nothing. fsai-livechat.js
       unhides it when it attaches. */
    var liveOptCard = root.querySelector('.fsai-opt--live');
    if (liveOptCard) liveOptCard.hidden = true;

    /* Fill the namespace rather than replace it: a module may already hold a
       reference to it from before the widget was built. */
    /* Named SHARED and not API on purpose: API already means the bot's base
       URL in this file, and declaring a second one inside init() shadowed it.
       Every request then went to "[object Object]/chat/stream". */
    for (var sharedKey in SHARED) {
      if (Object.prototype.hasOwnProperty.call(SHARED, sharedKey)) {
        window.FSAI[sharedKey] = SHARED[sharedKey];
      }
    }

    /* Anyone who was waiting. Shift as we go so a module cannot be started
       twice, and keep going if one of them throws. */
    var waiting = window.FSAI.onReady || [];
    while (waiting.length) {
      var ready = waiting.shift();
      try { ready(window.FSAI); }
      catch (e) {
        if (window.console && console.error) {
          console.error('[Flower Station] a chat module failed to start:', e);
        }
      }
    }

    root.querySelector('.fsai-opt--ai').addEventListener('click', function () {
      showView('julie');
    });

    backBtn.addEventListener('click', function () {
      /* The module gets first refusal: out of its enquiry screen, back is the
         screen that offered it, not all the way out to the chooser. */
      if (mod('onBack', view) === true) return;
      showView('choose');
    });

    launcher.addEventListener('click', open);
    closeBtn.addEventListener('click', close);
    panel.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
    });

    /* ------------------------------------------------------- her greeting
       The disc on its own is just a face; this is what tells you what she is
       for. It STAYS until the customer dismisses it or opens the chat - an
       earlier version retired itself after 11 seconds, which meant most
       people never saw any words at all.

       Dismissing it closes it for THIS page only - it greets again on the next
       page load. Nothing is persisted: an earlier version remembered the
       dismissal for the whole visit, which meant closing it once silenced her
       for good and there was no way to get the words back. */
    var hello = root.querySelector('.fsai-hello');

    function hideHello() {
      if (hello) hello.hidden = true;
    }

    function showHello() {
      // never greet over an already-open chat
      if (!hello || !panel.hidden || launcher.hidden) return;
      // ...or over a page's own checkout button - see data-hello-hide-on
      if (helloHiddenHere()) return;
      hello.hidden = false;
    }

    if (hello) {
      root.querySelector('.fsai-hello-open').addEventListener('click', open);
      root.querySelector('.fsai-hello-close').addEventListener('click', function (e) {
        e.stopPropagation();
        hideHello();
      });
      // let the page settle first; an instant popup reads as an ad
      window.setTimeout(showHello, 1800);
    }

    /* -------------------------------------------------------- transcript */

    /* Julie's portrait, built from the authored SVG constant. Each of her
       turns is prefixed with it so a reply reads as coming from someone. */
    function avatar() {
      var node = el('span', 'fsai-avatar');
      node.innerHTML = AVATAR;      // authored constant, never API data
      return node;
    }

    function addMessage(text, who, variant) {
      var wrap = el('div', 'fsai-msg fsai-msg--' + who + (variant ? ' fsai-msg--' + variant : ''));
      if (who === 'bot') wrap.appendChild(avatar());
      var bubble = el('div', 'fsai-bubble', text);
      wrap.appendChild(bubble);
      log.appendChild(wrap);
      scrollToEnd();
      return bubble;
    }

    /* A full reply takes about ten seconds, roughly four of them catalogue
       search before the first word exists. One frozen sentence for that long
       reads as a hang, so the label narrates what she is actually doing. */
    var TYPING_STEPS = [
      'Julie is reading your message…',
      'Looking through the shop…',
      'Checking what’s fresh today…',
      'Putting a few together…',
      'Almost ready…'
    ];

    function addTyping() {
      var wrap = el('div', 'fsai-msg fsai-msg--bot fsai-msg--typing');
      wrap.appendChild(avatar());

      var block = el('div', 'fsai-typing');
      var label = el('div', 'fsai-typing-label', TYPING_STEPS[0]);
      block.appendChild(label);

      var dots = el('div', 'fsai-dots');
      dots.appendChild(el('span', 'fsai-dot'));
      dots.appendChild(el('span', 'fsai-dot fsai-dot--2'));
      dots.appendChild(el('span', 'fsai-dot fsai-dot--3'));
      block.appendChild(dots);

      block.setAttribute('role', 'status');
      wrap.appendChild(block);
      log.appendChild(wrap);

      /* Advance through the steps, holding on the last one however long it
         takes. The timer id lives on the node so killTyping() can clear it -
         an orphaned interval would tick for the rest of the page's life. */
      var step = 0;
      wrap.fsaiTimer = window.setInterval(function () {
        if (step >= TYPING_STEPS.length - 1) return;
        step += 1;
        label.classList.add('fsai-typing-label--out');
        window.setTimeout(function () {
          label.textContent = TYPING_STEPS[step];
          label.classList.remove('fsai-typing-label--out');
        }, 320);
      }, 2400);

      scrollToEnd();
      return wrap;
    }

    function killTyping(node) {
      if (!node) return;
      if (node.fsaiTimer) { window.clearInterval(node.fsaiTimer); node.fsaiTimer = null; }
      node.remove();
    }

    /* One card. `card_type: "info"` is a link to a help page (delivery, care,
       returns) - never a purchasable product, so it gets no image, no price
       and a visibly different treatment. */
    function makeCard(card, opts) {
      if (!card || typeof card !== 'object') return null;
      var title = str(card.title);
      if (!title) return null;
      /* Info cards are the ones that carry contact details. A product title
         is left alone entirely. */
      if (card.card_type === 'info') title = ourNumber(title);
      var noOwnButton = !!(opts && opts.noOwnButton);

      var href = safeHref(card.url);
      var isInfo = card.card_type === 'info';
      /* An action the widget actually has. Unknown ones fall through to a
         plain card rather than a dead button. */
      var act = CARD_ACTIONS[str(card.action)] || null;

      /* The reminders card arrives as a LINK, not an action - the assistant
         has always sent ?reminders=open and nothing in the theme listens for
         it any more, so following it reloads the shop and opens nothing.

         Recognising the address here fixes that without waiting on the
         assistant to send anything new: the card becomes a button that opens
         the modal over the page. If a later version does send
         action: "open_reminders", the line above wins and this never runs. */
      if (!act && href && /[?&]reminders=open|#openRemPopup/i.test(href)) {
        act = CARD_ACTIONS.open_reminders || null;
      }
      /* Three of these stacked is a lot of scrolling on a phone, so a run of
         them collapses to one line each - title and CTA, no description. */
      var compact = isInfo && !!(card.compact || (opts && opts.compact));

      /* The card is a DIV wrapping a link rather than being a link itself, so
         the Add to cart button can sit BESIDE the link: a <button> inside an
         <a> is invalid HTML, and tapping it would also navigate away. */
      var node = el('div', 'fsai-card' + (isInfo ? ' fsai-card--info' : '') +
                          (compact ? ' fsai-card--compact' : ''));
      var main;
      if (act) {
        /* A real button: it does something on this page rather than going
           somewhere, and the keyboard and screen reader should be told that
           rather than being handed a link that goes nowhere. */
        main = el('button', 'fsai-card-main');
        main.type = 'button';
        main.addEventListener('click', act);
      } else {
        main = href ? el('a', 'fsai-card-main') : el('div', 'fsai-card-main');
      }
      /* However the assistant wrote it - tel:020..., tel:+44 20... - it goes
         out in the same form as the number in the footer. */
      if (href && /^tel:/i.test(href)) href = dialable(href);
      if (href && !act) {
        main.href = href;
        if (opensInNewTab(href)) {
          main.target = '_blank';
          main.rel = 'noopener noreferrer';
        }
      }

      if (!isInfo) {
        var src = safeImageSrc(card.image_url);
        if (src) {
          var img = el('img', 'fsai-thumb');
          img.src = src;
          img.alt = '';                 // decorative: the title carries the name
          // setAttribute, not the IDL property: a browser that does not
          // implement `loading` drops the property silently, and the attribute
          // is what the lazy-loading heuristic actually reads.
          img.setAttribute('loading', 'lazy');
          img.setAttribute('decoding', 'async');
          img.width = 62;
          img.height = 62;
          main.appendChild(img);
        } else {
          var ph = el('span', 'fsai-thumb fsai-thumb--empty');
          ph.innerHTML = ICON_BLOOM;    // authored constant, not API data
          main.appendChild(ph);
        }
      }

      var body = el('div', 'fsai-card-body');
      body.appendChild(el('div', 'fsai-card-title', title));

      /* What is behind a tel: or mailto: IS the information, so it survives
         compacting - written out by the assistant where it bothered to
         ("020 7724 7525 - 9am to 6pm"), otherwise straight off the href. */
      var detail = compact ? contactDetail(href) : '';
      var why = ourNumber(str(card.reason));
      /* Never the same thing twice: with the contact in the title, the line
         underneath is the description - and where the description IS the
         contact, it is dropped rather than repeated. */
      var second = why || detail;
      if (second && title.indexOf(second) !== -1) second = '';
      if (why && !compact) body.appendChild(el('div', 'fsai-card-why', why));
      else if (second) body.appendChild(el('div', 'fsai-card-why', second));

      if (!isInfo) {
        var price = priceLabel(card);
        if (price) body.appendChild(el('div', 'fsai-card-price', price));
        if (href) body.appendChild(el('div', 'fsai-card-cta', 'View →'));
      } else if (href || act) {
        body.appendChild(el('div', 'fsai-card-cta',
          str(card.cta) || (act ? 'Open →' : 'Learn more →')));
      }

      main.appendChild(body);
      node.appendChild(main);

      /* Info cards are help pages (delivery, care, returns) - not purchasable.
         Inside a bundle the items share ONE "Add all" button, so they skip
         their own; three stacked buttons per option was mostly noise. */
      if (!isInfo && !noOwnButton) attachAddToCart(node, href);

      return node;
    }

    /* =====================================================================
       Add to cart
       ---------------------------------------------------------------------
       The bot's API returns no variant ids (see ProductCard in
       webapp/server.py:971), and Shopify's cart API accepts nothing else, so
       they are fetched from the storefront on demand:
       GET {root}products/{handle}.js - same origin, ~100ms, live stock.

       One tap for a single-variant product; a picker for the rest. Roughly
       half the catalogue is multi-variant, so the picker is the normal path.
       The customer never leaves the conversation.
       ===================================================================== */
    function attachAddToCart(cardEl, href) {
      if (!href) return;                       // no url -> no button at all

      var actions = el('div', 'fsai-card-actions');
      var button = el('button', 'fsai-atc', 'Add to cart');
      button.type = 'button';
      actions.appendChild(button);

      var picker = el('div', 'fsai-variants');
      picker.hidden = true;

      var note = el('div', 'fsai-atc-msg');
      note.hidden = true;

      cardEl.appendChild(actions);
      cardEl.appendChild(picker);
      cardEl.appendChild(note);

      var variants = null;        // cached after the first successful fetch
      var busyHere = false;       // guards against double-taps adding twice

      function setNote(text, kind) {
        note.className = 'fsai-atc-msg fsai-atc-msg--' + kind;
        note.textContent = text;
        note.hidden = false;
        revealInLog(note);
      }

      function clearNote() { note.hidden = true; note.textContent = ''; }

      /* Never leave a dead button: if we cannot resolve variants, the card
         falls back to being exactly what it was - a link with "View ->". */
      function dropButton() {
        actions.remove();
        picker.remove();
        note.remove();
      }

      function fill(node, text, spinning) {
        while (node.firstChild) node.removeChild(node.firstChild);
        if (spinning) node.appendChild(el('span', 'fsai-spin'));
        node.appendChild(el('span', null, text));
      }

      /* Freeze every control in this card while a request is in flight, and
         hand back a restore(). Out-of-stock options stay disabled. */
      function freeze(control, label) {
        busyHere = true;
        var previous = [];
        while (control.firstChild) {
          previous.push(control.firstChild);
          control.removeChild(control.firstChild);
        }
        control.appendChild(el('span', 'fsai-spin'));
        control.appendChild(el('span', null, label));

        var options = Array.prototype.slice.call(picker.querySelectorAll('.fsai-variant'));
        options.forEach(function (opt) { opt.disabled = true; });
        button.disabled = true;

        return function restore() {
          busyHere = false;
          while (control.firstChild) control.removeChild(control.firstChild);
          previous.forEach(function (child) { control.appendChild(child); });
          options.forEach(function (opt) {
            opt.disabled = opt.getAttribute('data-fsai-oos') === '1';
          });
          button.disabled = false;
        };
      }

      function closePicker() {
        picker.hidden = true;
        while (picker.firstChild) picker.removeChild(picker.firstChild);
        button.classList.remove('fsai-atc--open');
        fill(button, 'Add to cart', false);
      }

      function openPicker(list) {
        while (picker.firstChild) picker.removeChild(picker.firstChild);
        picker.appendChild(el('div', 'fsai-variants-label', 'Choose an option'));

        list.forEach(function (variant) {
          var opt = el('button', 'fsai-variant');
          opt.type = 'button';
          opt.appendChild(el('span', 'fsai-variant-name', variant.title));

          if (variant.available) {
            if (variant.price != null) {
              opt.appendChild(el('span', 'fsai-variant-price', money(variant.price)));
            }
            opt.addEventListener('click', function () { addVariant(variant, opt); });
          } else {
            /* Shown, not hidden: the product page lists it too, and a card
               that silently omits an option looks like a bug. */
            opt.disabled = true;
            opt.setAttribute('data-fsai-oos', '1');
            opt.appendChild(el('span', 'fsai-variant-oos', 'Out of stock'));
          }
          picker.appendChild(opt);
        });

        picker.hidden = false;
        button.classList.add('fsai-atc--open');
        fill(button, 'Cancel', false);
        revealInLog(picker);
      }

      function proceed(list) {
        var sellable = list.filter(function (v) { return v.available; });
        if (!sellable.length) { dropButton(); return; }   // nothing to buy
        if (list.length === 1) { addVariant(list[0], button); return; }
        openPicker(list);
      }

      function onAddClick() {
        if (busyHere) return;
        if (!picker.hidden) { closePicker(); return; }     // toggle, no re-fetch
        clearNote();
        if (variants) { proceed(variants); return; }

        var jsUrl = productJsUrl(href);
        if (!jsUrl) { dropButton(); return; }

        var restore = freeze(button, 'Loading');
        fetch(jsUrl, { headers: { Accept: 'application/json' } })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
          })
          .then(function (payload) {
            var list = readVariants(payload);
            if (!list) throw new Error('no usable variants');
            variants = list;
            restore();
            proceed(list);
          })
          .catch(function () {
            restore();
            dropButton();       // 404 / non-JSON / offline: silent fallback
          });
      }

      function addVariant(variant, control) {
        if (busyHere) return;
        clearNote();
        var restore = freeze(control, 'Adding');

        var cart = themeCart();
        var form = new FormData();
        form.append('id', variant.id);
        form.append('quantity', '1');

        /* Ask for the cart sections so the drawer can re-render itself -
           exactly what product-form.js does. */
        if (cart && typeof cart.getSectionsToRender === 'function') {
          try {
            form.append('sections', cart.getSectionsToRender().map(function (s) {
              return s.id;
            }));
            form.append('sections_url', window.location.pathname);
            if (typeof cart.setActiveElement === 'function') {
              cart.setActiveElement(document.activeElement);
            }
          } catch (e) { /* sections are an optimisation, not a requirement */ }
        }

        var config = themeFetchConfig();
        config.method = 'POST';
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];   // FormData sets its own boundary
        config.body = form;

        fetch(cartAddTarget(config))
          .then(function (res) { return res.json(); })
          .then(function (data) {
            /* Inverted from what you would expect: /cart/add.js sets `status`
               ONLY on failure (a sold-out variant, a bad id). */
            if (!data || data.status) {
              themePublish('cartError', {
                source: 'fsai-chat',
                productVariantId: variant.id,
                errors: (data && (data.errors || data.description)) || null,
                message: data && data.message
              });
              setNote(str(data && data.description) ||
                      'Sorry, that one’s just gone out of stock.', 'err');
              return;
            }

            /* Paint the confirmation BEFORE the drawer opens. Opening it adds
               body.overflow-hidden, which hides this widget until the drawer
               closes - so the tick has to already be on the card, ready for
               when the customer comes back to the conversation. */
            setNote('✓ Added — ' + variant.title +
                    (variant.price != null ? ', ' + money(variant.price) : ''), 'ok');
            closePicker();

            /* Without cartUpdate the item genuinely IS in the basket but the
               header count never moves and the drawer never opens - customers
               read that as failure and tap again, adding duplicates.
               productVariantId is a STRING, matching what product-form.js
               publishes (formData.get('id')), because price-per-item.js
               compares it with === against a dataset value. */
            themePublish('cartUpdate', {
              source: 'fsai-chat',
              productVariantId: variant.id,
              cartData: data
            });

            refreshThemeCart(cart, data);
          })
          .catch(function () {
            setNote('That didn’t go through — do try again.', 'err');
          })
          .then(restore, restore);
      }

      button.addEventListener('click', onAddClick);
    }

    /* Hand the response to the theme so the drawer re-renders and opens. */
    function refreshThemeCart(cart, data) {
      if (!cart) return;

      /* On the cart template the theme renders the drawer with display:none
         (snippets/cart-drawer.liquid:22). Publishing cartUpdate has already
         made cart.js refresh that page, so leave the drawer alone. */
      try {
        if (cart.style && cart.style.display === 'none') return;
      } catch (e) { /* keep going */ }

      try { cart.classList.remove('is-empty'); } catch (e) { /* ignore */ }

      /* renderContents() assumes every section target exists, but
         #cart-icon-bubble is wrapped in `{% if cart != empty %}`
         (sections/header.liquid:444), so on the very first add to an empty
         cart it can be missing and throw before the drawer is opened. Fall
         back to opening the drawer directly in that case. */
      var rendered = false;
      try {
        if (typeof cart.renderContents === 'function') {
          cart.renderContents(data);
          rendered = true;
        }
      } catch (e) { /* fall through to open() */ }

      if (!rendered) {
        try { if (typeof cart.open === 'function') cart.open(); } catch (e) { /* ignore */ }
      }
    }

    function makeCardList(cards, opts) {
      var list = el('div', 'fsai-cards');
      var added = 0;
      /* The backend does not have to ask: three help cards in a row is where
         the scrolling starts to hurt, and the widget is the thing that knows
         how many arrived. */
      var many = arr(cards).filter(function (c) {
        return c && c.card_type === 'info';
      }).length > 2;
      var pass = many ? Object.assign({}, opts || {}, { compact: true }) : opts;
      arr(cards).forEach(function (c) {
        var node = makeCard(c, pass);
        if (node) { list.appendChild(node); added += 1; }
      });
      /* Not inside a bundle: those items are one offer presented together and
         hiding half of it would misrepresent the price. */
      if (added > MAX_CARDS_SHOWN && !(opts && opts.noOwnButton)) {
        collapseTail(list, MAX_CARDS_SHOWN);
      }
      return added ? list : null;
    }

    /* Four is where the list stops being scannable - roughly the height of
       the answer above it on a phone. */
    var MAX_CARDS_SHOWN = 4;

    function collapseTail(list, keep) {
      var rest = [].slice.call(list.children, keep);
      if (!rest.length) return;
      rest.forEach(function (node) { node.classList.add('fsai-card--folded'); });

      var more = el('button', 'fsai-cards-more');
      more.type = 'button';
      more.textContent = 'Show ' + rest.length + ' more';
      more.addEventListener('click', function () {
        rest.forEach(function (node) {
          node.classList.remove('fsai-card--folded');
        });
        /* One way only. Collapsing them again under the reader's cursor
           would move everything they were about to tap. */
        if (more.parentNode) more.parentNode.removeChild(more);
      });
      list.appendChild(more);
    }

    /* Bundles are ALTERNATIVE options, not one basket - each gets its own
       group, its own name and its own total. */
    function makeBundle(bundle, index) {
      if (!bundle || typeof bundle !== 'object') return null;
      var cards = makeCardList(bundle.items, { noOwnButton: true });
      if (!cards) return null;

      var group = el('div', 'fsai-bundle');
      var head = el('div', 'fsai-bundle-head');
      head.appendChild(el('span', 'fsai-bundle-tag', 'Option ' + (index + 1)));
      head.appendChild(el('span', 'fsai-bundle-name', str(bundle.name) || 'Bundle'));
      if (isNum(bundle.total)) {
        head.appendChild(el('span', 'fsai-bundle-total', money(bundle.total)));
      }
      group.appendChild(head);

      var why = str(bundle.reason);
      if (why) group.appendChild(el('div', 'fsai-bundle-why', why));

      group.appendChild(cards);
      attachBundleAddAll(group, bundle);
      return group;
    }

    /* =====================================================================
       "Add all N — £total" for a bundle
       ---------------------------------------------------------------------
       A bundle is one curated gift set, so it gets ONE button that puts every
       item in the basket in a SINGLE request - /cart/add.js takes an `items`
       array, so this is one round trip, not one per item.

       Which variant per item: the backend builds a bundle total as
       sum(price_min) over its items (query_router.py:2039), i.e. each item's
       CHEAPEST variant. Picking that same variant is what makes the basket
       agree with the total printed on the card.
       ===================================================================== */
    function attachBundleAddAll(group, bundle) {
      var items = arr(bundle.items).filter(function (it) {
        return it && typeof it === 'object' && it.card_type !== 'info' && safeHref(it.url);
      });
      if (!items.length) return;

      var foot = el('div', 'fsai-bundle-foot');
      var button = el('button', 'fsai-atc fsai-bundle-add');
      button.type = 'button';
      /* Just "Add to cart" - the count and the total are already printed in
         the bundle header right above, so repeating them read as noise. */
      var label = 'Add to cart';
      button.textContent = label;
      button.setAttribute('aria-label',
        'Add all ' + items.length + ' items in ' + (str(bundle.name) || 'this set') + ' to cart');

      var note = el('div', 'fsai-atc-msg');
      note.hidden = true;

      foot.appendChild(button);
      foot.appendChild(note);
      group.appendChild(foot);

      var busyHere = false;
      var resolved = null;          // cached [{card, variant}] after a good fetch

      function setNote(text, kind) {
        note.className = 'fsai-atc-msg fsai-atc-msg--' + kind;
        note.textContent = text;
        note.hidden = false;
        revealInLog(note);
      }

      function freeze(text) {
        busyHere = true;
        button.disabled = true;
        while (button.firstChild) button.removeChild(button.firstChild);
        button.appendChild(el('span', 'fsai-spin'));
        button.appendChild(el('span', null, text));
        return function restore() {
          busyHere = false;
          button.disabled = false;
          while (button.firstChild) button.removeChild(button.firstChild);
          button.textContent = label;
        };
      }

      /* The variant that made the printed total, if it is still in stock;
         otherwise the cheapest one that is. */
      function pickVariant(card, variants) {
        var sellable = arr(variants).filter(function (v) { return v.available; });
        if (!sellable.length) return null;
        if (isNum(card.price_min)) {
          for (var i = 0; i < sellable.length; i++) {
            if (sellable[i].price != null &&
                Math.abs(sellable[i].price - card.price_min) < 0.005) return sellable[i];
          }
        }
        var cheapest = sellable[0];
        sellable.forEach(function (v) {
          if (v.price != null && (cheapest.price == null || v.price < cheapest.price)) {
            cheapest = v;
          }
        });
        return cheapest;
      }

      function onClick() {
        if (busyHere) return;
        note.hidden = true;
        if (resolved) { send(resolved); return; }

        var restore = freeze('Checking stock');
        Promise.all(items.map(function (card) {
          var jsUrl = productJsUrl(safeHref(card.url));
          if (!jsUrl) return Promise.resolve({ card: card, variants: null });
          return fetch(jsUrl, { headers: { Accept: 'application/json' } })
            .then(function (res) {
              if (!res.ok) throw new Error('HTTP ' + res.status);
              return res.json();
            })
            .then(function (payload) {
              return { card: card, variants: readVariants(payload) };
            })
            .catch(function () { return { card: card, variants: null }; });
        })).then(function (results) {
          restore();

          var picks = [];
          var missing = [];
          results.forEach(function (r) {
            var variant = r.variants ? pickVariant(r.card, r.variants) : null;
            if (variant) picks.push({ card: r.card, variant: variant });
            else missing.push(str(r.card.title) || 'an item');
          });

          /* Never silently add a partial set - the customer chose a bundle at
             a price, so tell them which piece is unavailable and let them add
             the rest deliberately. */
          if (!picks.length) {
            setNote('Sorry, this set isn’t available at the moment.', 'err');
            return;
          }
          if (missing.length) {
            setNote('Out of stock: ' + missing.join(', ') +
                    '. Tap again to add the other ' + picks.length +
                    (picks.length === 1 ? ' item.' : ' items.'), 'err');
            resolved = picks;       // a second tap adds what IS available
            return;
          }
          resolved = picks;
          send(picks);
        });
      }

      function send(picks) {
        var restore = freeze('Adding');
        var cart = themeCart();

        /* JSON with an `items` array, not FormData: one request for the whole
           set. Same shape custom-add-on.js uses for the theme's own add-ons. */
        var payload = {
          items: picks.map(function (p) {
            return { id: Number(p.variant.id), quantity: 1 };
          })
        };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          try {
            payload.sections = cart.getSectionsToRender().map(function (s) {
              return s.id;
            }).join(',');
            payload.sections_url = window.location.pathname;
            if (typeof cart.setActiveElement === 'function') {
              cart.setActiveElement(document.activeElement);
            }
          } catch (e) { /* sections are an optimisation */ }
        }

        var config = themeFetchConfig();
        config.method = 'POST';
        config.headers = config.headers || {};
        config.headers['Content-Type'] = 'application/json';   // a JSON body this time
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(payload);

        // Request object, not a url string - see cartAddTarget().
        fetch(cartAddTarget(config))
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (!data || data.status) {
              themePublish('cartError', {
                source: 'fsai-chat',
                productVariantId: String(picks[0].variant.id),
                errors: (data && (data.errors || data.description)) || null,
                message: data && data.message
              });
              setNote(str(data && data.description) ||
                      'That didn’t go through — do try again.', 'err');
              return;
            }

            /* Total from the variants actually added, so a substitution is
               visible rather than quietly disagreeing with the card. */
            var spent = 0, known = true;
            picks.forEach(function (p) {
              if (p.variant.price == null) known = false; else spent += p.variant.price;
            });
            setNote('✓ Added ' + picks.length +
                    (picks.length === 1 ? ' item' : ' items') +
                    (known ? ' — ' + money(spent) : ''), 'ok');

            themePublish('cartUpdate', {
              source: 'fsai-chat',
              productVariantId: String(picks[0].variant.id),
              cartData: data
            });
            refreshThemeCart(cart, data);
          })
          .catch(function () {
            setNote('That didn’t go through — do try again.', 'err');
          })
          .then(restore, restore);
      }

      button.addEventListener('click', onClick);
    }

    /* Chips belong to the LATEST turn only. Leaving old rows behind gives a
       trail of stale buttons up the transcript that re-ask answered questions. */
    function clearChips() {
      root.querySelectorAll('.fsai-chips').forEach(function (row) { row.remove(); });
    }

    function addChips(suggestions) {
      var texts = arr(suggestions).map(str).filter(Boolean);
      if (!texts.length) return;
      clearChips();
      var row = el('div', 'fsai-chips');
      texts.slice(0, 6).forEach(function (text) {
        var chip = el('button', 'fsai-chip', text);
        chip.type = 'button';
        chip.addEventListener('click', function () {
          if (busy) return;
          input.value = text;
          submit();
        });
        row.appendChild(chip);
      });
      log.appendChild(row);
      scrollToEnd();
    }

    /* Attach the payload of a `done` event (or a restored turn) to a bubble. */
    function attachPayload(bubble, data) {
      arr(data.bundles).forEach(function (b, i) {
        var group = makeBundle(b, i);
        if (group) bubble.appendChild(group);
      });
      var cards = makeCardList(data.products);
      if (cards) bubble.appendChild(cards);
      scrollToEnd();
    }

    function hideIntro() {
      var intro = root.querySelector('.fsai-intro');
      if (intro) intro.remove();
    }

    addChips(STARTERS);

    /* --------------------------------------------------------- networking */

    function postChat(path, question, signal) {
      return fetch(API + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question, session_id: sessionId }),
        signal: signal
      });
    }

    function rememberSession(id) {
      if (typeof id !== 'string' || !id) return;
      sessionId = id;
      writeStore(SESSION_KEY, id);
    }

    /* 429 comes back in the normal response shape with a customer-facing
       sentence in `answer`. It must NOT fall into the generic error branch -
       a customer who typed quickly would be told the shop is broken. */
    var RATE_LIMIT_FALLBACK =
      'You’ve sent me quite a few messages — give me a moment and try again.';

    function rateLimitMessage(res) {
      return res.json().then(function (body) {
        return str(body && body.answer) || RATE_LIMIT_FALLBACK;
      }, function () {
        return RATE_LIMIT_FALLBACK;
      });
    }

    /* Shown when we throttle locally, before anything is sent. Worded to make
       clear the message is still sitting in the box, unlike the server's 429
       which fires on a message it has already received and refused. */
    function showThrottleNote() {
      var existing = root.querySelector('.fsai-msg--throttle');
      if (existing) existing.remove();          // don't stack copies
      var bubble = addMessage(
        'That’s a lot of questions at once — give me a few seconds to keep up. ' +
        'I haven’t sent that one yet, so press send again in a moment.',
        'bot', 'note');
      bubble.parentNode.classList.add('fsai-msg--throttle');
    }

    function submit() {
      var question = input.value.trim();
      if (!question || busy) return;

      /* Trip before the server does, so the customer gets the friendly
         sentence instead of an unreadable cross-origin 429. The typed text is
         left in the box on purpose - nothing was sent, so there is nothing to
         retype. */
      var sends = recentSends();
      if (sends.length >= BURST_LIMIT) {
        hideIntro();
        showThrottleNote();
        return;
      }
      recordSend(sends);

      hideIntro();
      clearChips();
      var stale = root.querySelector('.fsai-msg--throttle');
      if (stale) stale.remove();        // its "not sent yet" wording is now wrong
      addMessage(question, 'user');
      input.value = '';
      clearStore(DRAFT_KEY);
      setBusy(true);                    // immediate: the backend takes ~10s

      var typing = addTyping();
      var streamBubble = null;
      var controller = ('AbortController' in window) ? new AbortController() : null;
      var timer = window.setTimeout(function () {
        if (controller) controller.abort();
      }, REQUEST_TIMEOUT_MS);

      /* The bubble is created on the FIRST token and reused for every one
         after it. A bubble per token gives a column of single words. */
      var pinnedOnce = false;

      function streamTarget() {
        if (!streamBubble) {
          if (typing) { killTyping(typing); typing = null; }
          /* From here until the answer is finished, nothing drags the
             transcript to the bottom - not the cards, not the chips. */
          holdTranscript(true);
          streamBubble = addMessage('', 'bot');
          streamBubble.classList.add('fsai-bubble--streaming');
        }
        return streamBubble;
      }

      /* Pinned on the first text, not on the empty bubble: an element with no
         content has no height, and the previous version measured it before
         there was anything in it and gave up. */
      function pinOnce() {
        if (pinnedOnce || !streamBubble) return;
        pinnedOnce = true;
        pinToStart(streamBubble);
      }

      /* How the answer READS as it arrives.

         The service streams in whatever sized pieces the model and the network
         hand it, which is often a few large bursts rather than a word at a
         time - so the text landed in slabs instead of being typed. Holding the
         text and revealing it at a steady rate makes it read as someone
         writing, whatever shape it arrived in.

         The rate is proportional, so a big backlog drains faster and a quick
         answer never feels padded: a few hundred characters land in about a
         second either way. */
      var pending = '';
      var drainTimer = null;
      var onDrained = null;
      var TYPE_OUT = MOTION === 'always' ||
        !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

      function stopDraining() {
        if (drainTimer) { window.clearInterval(drainTimer); drainTimer = null; }
      }

      function flushPending() {
        stopDraining();
        if (pending) { streamTarget().textContent += pending; pending = ''; }
        var done = onDrained;
        onDrained = null;
        if (done) done();
      }

      function pushText(text) {
        if (!TYPE_OUT) { streamTarget().textContent += text; pinOnce(); return; }
        pending += text;
        streamTarget();                       // create the bubble on the first token
        if (drainTimer) return;
        drainTimer = window.setInterval(function () {
          if (!pending) { flushPending(); return; }
          var take = Math.max(2, Math.ceil(pending.length / 8));
          streamTarget().textContent += pending.slice(0, take);
          pending = pending.slice(take);
          pinOnce();
        }, 24);
      }

      /* Nothing after the answer - the cards, the chips - should appear before
         the sentence they belong to has finished. */
      function whenDrained(run) {
        if (!pending) { stopDraining(); run(); return; }
        onDrained = run;
      }

      function finish(data) {
        if (!data || typeof data !== 'object') data = {};
        rememberSession(data.session_id);
        var answer = str(data.answer);
        if (streamBubble) {
          streamBubble.classList.remove('fsai-bubble--streaming');
          // Prefer the complete answer: the trailing chunk can be held back.
          if (answer && answer.length >= streamBubble.textContent.length) {
            streamBubble.textContent = answer;
          }
        } else {
          /* No tokens arrived - the whole answer came with `done`. It still
             belongs at the top rather than dropped in at the bottom. */
          holdTranscript(true);
          if (typing) { killTyping(typing); typing = null; }
          streamBubble = addMessage(
            answer || 'Sorry, I didn’t quite catch that — could you say it again?', 'bot');
          pinOnce();
        }
        /* Both of these scroll to the end of their own accord, so the hold has
           to outlast them. Releasing before this point was what still yanked
           the transcript to the bottom the moment an answer finished. */
        attachPayload(streamBubble, data);
        addChips(data.suggestions);
        holdTranscript(false);
      }

      function fail(message) {
        /* However this turn ends, the transcript has to move again - a hold
           left on would freeze scrolling for the rest of the session. */
        holdTranscript(false);
        if (typing) { killTyping(typing); typing = null; }
        if (streamBubble) {
          streamBubble.classList.remove('fsai-bubble--streaming');
          if (streamBubble.textContent) return;   // partial text already delivered
          streamBubble.parentNode.remove();
          streamBubble = null;
        }
        addMessage(message, 'bot', 'note');
      }

      function done() {
        window.clearTimeout(timer);
        setBusy(false);
        if (!panel.hidden && !isTouch) input.focus();
      }

      /* Non-streaming fallback: same request, same response fields, but the
         customer waits the full ~10s with nothing to read. Only used where
         ReadableStream is unavailable. */
      function viaPlainChat() {
        return postChat('/chat', question, controller && controller.signal)
          .then(function (res) {
            if (res.status === 429) {
              return rateLimitMessage(res).then(fail);
            }
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json().then(finish);
          });
      }

      postChat('/chat/stream', question, controller && controller.signal)
        .then(function (res) {
          if (res.status === 429) {
            return rateLimitMessage(res).then(fail);
          }
          if (!res.ok) throw new Error('HTTP ' + res.status);
          if (!res.body || typeof res.body.getReader !== 'function') {
            return viaPlainChat();      // old browser: no streaming
          }

          var reader = res.body.getReader();
          var decoder = new TextDecoder();
          var buffer = '';
          var final = null;

          function handleLine(line) {
            if (line.indexOf('data:') !== 0) return;
            var payload = line.slice(5).trim();
            if (!payload) return;
            var ev;
            try { ev = JSON.parse(payload); } catch (e) { return; }
            if (!ev || typeof ev !== 'object') return;

            if (ev.type === 'token') {
              if (typeof ev.text !== 'string' || !ev.text) return;
              /* No scroll here: the answer was pinned when it appeared and the
                 reader stays where they are. */
              pushText(ev.text);
            } else if (ev.type === 'done') {
              final = ev;
            }
          }

          function pump() {
            return reader.read().then(function (chunk) {
              if (chunk.done) {
                buffer += decoder.decode();
                if (buffer) {
                  buffer.split(/\r?\n/).forEach(handleLine);
                  buffer = '';
                }
                if (final) { whenDrained(function () { finish(final); }); return; }
                /* A proxy that buffers or truncates SSE can end the stream
                   without a `done` event. Keep whatever text arrived rather
                   than replacing a real answer with an error. */
                if (streamBubble && (streamBubble.textContent || pending)) {
                  whenDrained(function () {
                    streamBubble.classList.remove('fsai-bubble--streaming');
                    holdTranscript(false);
                  });
                  return;
                }
                throw new Error('stream ended without a done event');
              }
              buffer += decoder.decode(chunk.value, { stream: true });
              var lines = buffer.split(/\r?\n/);
              buffer = lines.pop();     // keep the partial line for next chunk
              lines.forEach(handleLine);
              return pump();
            });
          }

          return pump();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') {
            fail('Sorry, that’s taking me longer than it should. Do try again — or ring the shop ' +
                 'on ' + PHONE + ' and one of us will help.');
            return;
          }
          /* An opaque TypeError is what a cross-origin 429 looks like from
             here, because that response carries no CORS headers. If the
             customer has been sending steadily, the rate limit is far more
             likely than an outage - say the kind thing. */
          if (err && err.name === 'TypeError' && recentSends().length >= 4) {
            fail(RATE_LIMIT_FALLBACK);
            return;
          }
          fail('I can’t reach the shop just now, sorry. Try me again in a moment — or ring ' +
               PHONE + ' and we’ll sort it out.');
        })
        .then(done, done);
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit();
    });

    /* ----------------------------------------------------- resume a chat
       Replays stored turns through the SAME makeCard()/addChips() path the
       live stream uses, so a restored card is indistinguishable from a fresh
       one. Deliberately fail-soft: offline, expired session, blocked storage
       or a 503 from the history store must all leave the customer with a
       normal empty chat, never a broken widget. */
    function restoreHistory() {
      if (historyRequested) return;
      historyRequested = true;
      if (!sessionId) return;

      fetch(API + '/chat/history/' + encodeURIComponent(sessionId) +
            '?limit=50&include_metadata=true')
        .then(function (res) {
          if (res.status === 400) {
            // Not a UUID - a stale id format. Drop it and start clean.
            clearStore(SESSION_KEY);
            sessionId = null;
            return null;
          }
          if (!res.ok) return null;     // unknown session returns [], not 404
          return res.json();
        })
        .then(function (data) {
          var messages = arr(data && data.messages);
          if (!messages.length) return; // expired server-side, or brand new

          // Only clear the intro once we KNOW there is something to show.
          hideIntro();
          clearChips();

          var lastSuggestions = null;
          messages.forEach(function (m) {
            var content = str(m && m.content);
            var meta = (m && m.metadata) || {};
            if (m && m.role === 'user') {
              if (content) addMessage(content, 'user');
            } else {
              attachPayload(addMessage(content, 'bot'), meta);
              lastSuggestions = meta.suggestions;
            }
          });
          addChips(lastSuggestions);
          scrollToEnd();
        })
        .catch(function () { /* offline or server down: start clean */ });
    }

    /* Reopen where the customer left off after a refresh or a navigation to a
       product page. Skipped on narrow viewports, where the panel is close to
       full-screen and popping it open over the storefront would be intrusive.

       Coming back from signing in is the exception: they left the page on
       purpose and expect to land back in the chat, so honour it on a phone
       too. The marker is cleared immediately - it applies to that one trip. */
    /* One status call on load so the chooser can be truthful the moment it
       opens, then a poll while the panel is shut. Never a conversation. */
    mod('boot');

    var returning = readStore(RETURNING_KEY) === '1';
    if (returning) clearStore(RETURNING_KEY);

    if (readStore(OPEN_KEY) === '1' && (returning || window.innerWidth > 750)) {
      open();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
